import clsx from 'clsx';
import {
  FRASE_DA_MOEDA_NAO_DECLARADA,
  MARCA_DA_MOEDA_NAO_DECLARADA,
  moedaExibidaNaLinha,
  moedasNaOrdem,
} from '@vestigo/core/traco-de-dinheiro';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link } from 'react-router-dom';
import { mesclar } from '../lib/mesclar';
import { fundoDoCartao, type TomDeCartao } from '../lib/paleta';

/**
 * Primitivas do design system, no vocabulário da referência: nenhum
 * border-radius, bordas hairline `dark/10`, micro-labels em versalete espaçado,
 * botão sólido escuro que vira accent no hover.
 */

/** Entrada padrão da referência: sobe, desfoca e aparece. */
export const fadeInUp = {
  hidden: { opacity: 0, y: 20, filter: 'blur(6px)' },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.6, ease: 'easeOut' as const, delay },
  }),
};

export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
      custom={delay}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Rótulo de apoio em versalete.
 *
 * Já foi `10px / 0.35em / dark-40`. Bonito parado, ilegível em uso: nessa
 * escala o espaçamento de letra desmonta a palavra e 40% de tinta some no fundo
 * claro. Um painel operacional é lido de relance, não contemplado — daí 11px,
 * tracking pela metade e contraste suficiente para o olho pegar sem focar.
 *
 * ## A TINTA SUBIU DE `/60` PARA `/75`, E É MEDIÇÃO, NÃO GOSTO
 *
 * Varredura de 26/08/2026 sobre todo nó folha de `<main>`: **17 de 33 nós de
 * texto da porta** e **173 de 307 em `/c/:slug`** estavam abaixo do piso de
 * 4,5:1 do WCAG AA. Este rótulo sozinho é **11 dos 17 da porta**, a 3,55:1 —
 * exatamente a faixa em que todo sistema operacional desenha um controle
 * *desabilitado*. O painel inteiro estava escrito com a tinta de um botão
 * apagado.
 *
 * **Por que `/75` e não o `/70` que a direção prescreveu.** `/70` mede 4,66:1
 * sobre `surface` — e este rótulo também vive direto sobre `desk`, que é o
 * fundo do `<body>`, onde ele dá **4,48:1** e reprova por 0,02. `/75` mede
 * **5,14 · 5,37 · 5,62** sobre desk · surface · muted e passa nos três fundos
 * do painel. Um degrau de opacidade não é token (a `R1` proíbe cor, raio,
 * sombra, curva, duração, família e biblioteca), e a alternativa — dois
 * degraus, um por fundo — quebraria a gramática para economizar 0,25 de
 * contraste.
 *
 * Cerca: `a-tinta-nao-volta-a-desabilitada.test.ts`, que calcula o piso a
 * partir dos hex da paleta em vez de repetir o número aqui.
 */
export function MicroLabel({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  /** O que o rótulo nomeia, quando o nome sozinho não basta para saber a unidade. */
  title?: string;
}) {
  return (
    <span
      title={title}
      className={mesclar(
        'text-[11px] font-bold uppercase tracking-[0.16em] text-dark/75',
        // O cursor é o único sinal de que há explicação atrás do rótulo; sem
        // ele, um `title` num texto estático nunca é descoberto.
        title && 'cursor-help',
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Cabeçalho da página.
 *
 * ## Por que ele encolheu
 *
 * Numa tela de 1240px, o caminho até o primeiro dado gastava cerca de 700px —
 * mais da metade do monitor — e este bloco era a maior parcela evitável. Ele
 * dizia em 40px de Oswald exatamente o que a aba ativa da barra logo acima já
 * dizia, precedido de um kicker com 12px de respiro e seguido de 32px de
 * margem. Três linhas de altura para repetir uma informação.
 *
 * Agora é um bloco de duas linhas curtas: a trilha (de onde vim · o que é isto)
 * numa linha de 10px, e o nome da tela em 30px. O título continua sendo o maior
 * texto da página — ele ainda ancora a leitura —, só deixou de ser um cartaz.
 *
 * `backTo` existe para o link de voltar entrar AQUI em vez de flutuar 40px
 * acima. Ele é a terceira repetição do mesmo lugar; como parte da trilha, passa
 * a custar zero altura.
 */
export function PageTitle({
  children,
  kicker,
  backTo,
  backLabel,
  className,
  'data-forma': endereco,
}: {
  children: ReactNode;
  /**
   * O endereço do bloco, encaminhado pelo mesmo motivo do `Card` e do
   * `MetricGrid`: sem esta prop, toda tela que precisa nomear o cabeçalho
   * embrulha a peça numa `<div>` que não desenha nada — e o invólucro some no
   * primeiro reagrupamento do leiaute, levando a cerca junto.
   *
   * Nasceu com a decisão nº 82 do `forma-juiz` (31/08/2026): toda tela que uma
   * leva toca sai dela com os blocos de primeiro nível ≥40px endereçados.
   */
  'data-forma'?: string;
  /**
   * Gêmeo do `className` da `PageNote`, e pelo mesmo defeito: quem compõe o
   * título dentro de outra peça precisava anular o `mb-5` por fora.
   */
  className?: string;
  /**
   * A trilha, em caixa alta.
   *
   * `ReactNode` e não `string` porque a caixa alta é do BLOCO, e nem tudo que
   * cabe numa trilha suporta ser gritado: um identificador que distingue
   * maiúscula de minúscula lido daqui vira um 404 (ver `pop/IdDaPeca.tsx`).
   * Quem precisa escapar da caixa manda um nó próprio.
   */
  kicker?: ReactNode;
  /** Destino do "voltar". Sem ele, a trilha mostra só o kicker. */
  backTo?: string;
  backLabel?: string;
}) {
  return (
    <div data-forma={endereco} className={mesclar('mb-5', className)}>
      {(kicker || backTo) && (
        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-dark/75">
          {backTo && (
            <Link
              to={backTo}
              className="inline-flex items-center gap-1 transition-colors hover:text-accent"
            >
              <ChevronLeft size={12} />
              {backLabel ?? 'Voltar'}
            </Link>
          )}
          {backTo && kicker && (
            <span aria-hidden className="text-dark/25">
              ·
            </span>
          )}
          {kicker && <span>{kicker}</span>}
        </div>
      )}
      <h1 className="font-display text-2xl font-semibold uppercase tracking-tighter md:text-3xl">
        {children}
      </h1>
    </div>
  );
}

/**
 * A VOLTA — uma peça só, com o destino vindo de dado.
 *
 * ## O defeito que ela conserta
 *
 * Nove sítios do painel construíam a saída à mão, cada um com o próprio
 * desenho e o próprio destino. Nas duas casas medidas em produção (V-Send e
 * V-Blogs, 25/08/2026) o resultado era o mesmo: um link de `innerText` vazio —
 * uma seta com `aria-label` e nada escrito. O operador via um ícone e tinha de
 * adivinhar para onde ele levava, ou passar o mouse e esperar o `title`.
 *
 * Por isso o **rótulo é visível e obrigatório**, e por isso ele nomeia o
 * DESTINO e não o gesto: «Matérias» diz para onde se vai; «Voltar» diz o que
 * todo mundo já sabe que a seta faz. O precedente é do próprio produto —
 * `blog/Editor.tsx` era o único dos nove que já escrevia o destino ao lado da
 * seta, e é o desenho que os outros oito passam a ter.
 *
 * ## Por que `para` OU `aoVoltar`
 *
 * A saída quase sempre é uma URL, e uma URL é colável, tem `middle-click` e
 * sobrevive ao botão do navegador. Mas o editor do V-Blogs fecha uma tela que
 * mora dentro da casa, sem trocar de rota — e forçá-lo a inventar uma rota
 * seria o desenho seguindo a peça em vez do contrário.
 *
 * `className` existe para as casas que têm tinta própria (`--sn-lapis` no
 * V-Send, `--bl-apagado` no V-Blogs): a peça é a mesma, a cor continua sendo a
 * do cômodo. Nenhum token novo — os dois já existem.
 */
export function Voltar({
  para,
  aoVoltar,
  rotulo,
  className,
}: {
  /** O destino. Ausente só quando `aoVoltar` assume. */
  para?: string;
  /** Fecha uma tela que não é rota. Ignorado quando há `para`. */
  aoVoltar?: () => void;
  /** O NOME do lugar para onde se volta — visível, nunca só `aria-label`. */
  rotulo: string;
  className?: string;
}) {
  const conteudo = (
    <>
      <ArrowLeft size={14} aria-hidden />
      <span className="truncate">{rotulo}</span>
    </>
  );
  const classe = clsx(
    'inline-flex min-w-0 shrink-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] transition-colors',
    // A volta é serviço, não é chamada — e serviço também se lê. Era `/55`
    // (3,12:1) por hierarquia; a hierarquia agora está na CAIXA e no tamanho,
    // e a tinta fica no piso derivado. Quem tem tinta própria manda pelo
    // `className`.
    !className && 'text-dark/75 hover:text-accent',
    className,
  );

  if (para) {
    return (
      <Link data-forma="voltar" to={para} className={classe}>
        {conteudo}
      </Link>
    );
  }
  return (
    <button data-forma="voltar" type="button" onClick={aoVoltar} className={classe}>
      {conteudo}
    </button>
  );
}

/** Um degrau da trilha. Sem `para`, é onde a pessoa está. */
export interface NivelDaTrilha {
  rotulo: ReactNode;
  /** Ausente = o nível atual, que não é link para si mesmo. */
  para?: string;
  title?: string;
  /**
   * A partir de que largura este nível é desenhado.
   *
   * Existe por uma medição, não por gosto: a 390 px o par «chevron + nome da
   * agência» vazava da moldura por cima dos atalhos (achado 17 da Rodada 1 do
   * Send), e o conserto foi acender os níveis por largura — do fim para o
   * começo, deixando o primeiro sempre de pé. Sem este campo, a trilha unificada
   * reintroduziria o defeito na primeira tela estreita.
   */
  desde?: 'xs' | 'sm';
  /**
   * O nível não encolhe nem trunca — é o chão da moldura.
   *
   * Só o logotipo usa: ele é `img`, e uma imagem fluida tem contribuição de
   * min-content ZERO. Sem `min-width: max-content` aqui o bloco inteiro perde o
   * piso e a trilha volta a sobrepor os atalhos (§61.1).
   */
  fixo?: boolean;
}

/**
 * ── O DEGRAU COM GALHO — A GRAMÁTICA DA TRILHA, NUMA PEÇA SÓ ────────────────
 *
 * ## O defeito que ela conserta, com o número
 *
 * A trilha do cabeçalho tinha **três affordâncias para três níveis**: a agência
 * era link, o cliente era um `<button>` que abria uma lista, e a propriedade
 * era uma placa de tinta cheia numa faixa de baixo. Medido em 26/08/2026, em
 * `/c/:slug`, `/c/:slug/p/:prop/gatilhos` e `/c/:slug/p/:prop/seo`:
 *
 * ```
 *   header a[href="/c/openclaw-e-hermes"]         → null   (a `V3` reprovando)
 *   header a[href="/c/…/p/autonext-lp"]           → null
 *   links dentro de [data-forma="trilha"]         → 2, os dois para "/"
 * ```
 *
 * O nome do cliente **estava** no cabeçalho e não levava ao cliente. Quem
 * aprendeu a voltar num nível não tinha aprendido nos outros.
 *
 * ## A regra, e ela é a única coisa que esta peça existe para impor
 *
 * > **o nome é o link daquele nível · o galho é o irmão daquele nível.**
 *
 * Duas cópias de trinta linhas com essa regra é exatamente como nasceram as
 * nove setas de «voltar» que esta família de obras está apagando — então a
 * gramática saiu de dentro do `SeletorDeCliente` e virou peça **antes** de
 * existir o segundo dono. Hoje ela tem dois: o cliente e a propriedade.
 *
 * ## O que ela NÃO faz
 *
 * Não desenha o painel que o galho abre. O cliente abre uma lista com filtro
 * (`SeletorDeCliente`); a propriedade abre a `LaminaDaBarra` por portal, porque
 * o cabeçalho é `.vst-casca` e um ancestral com `backdrop-filter` vira bloco de
 * contenção de `position: fixed` — o diagnóstico inteiro está em
 * `LaminaDaBarra.tsx`. Dois painéis, uma gramática: é o `children`.
 *
 * ## `hidden sm:flex`, e o ponto é HERDADO e não escolhido
 *
 * O bloco da esquerda do cabeçalho **não tem `min-w-0`**, de propósito: é a
 * ausência dele que dá piso ao logotipo (§61.1). Sem esse escape, o piso do
 * bloco passa a incluir a largura inteira deste controle e a página rola de
 * lado — medido, com nome comprido: **43px a 360, 13 a 390, 182 a 420 e 102 a
 * 500**. Abaixo de 640 o cabeçalho volta a ser logotipo, atalhos e sair.
 *
 * ## A cor diz a profundidade, e o tamanho diz o nível
 *
 * `atual` é o degrau mais fundo: ele fica em **accent**, que é a lei que a
 * `Trilha` já aplica ao nível sem `para`. Os degraus acima ficam em
 * `dark/85` — 7,14:1 sobre o vidro da casca, contra 6,49:1 do accent, os dois
 * acima do piso de 4,5:1. Sem isto os três nomes gritam juntos e a trilha deixa
 * de ser um caminho.
 */
export function DegrauComGalho({
  nome,
  para,
  rotuloVazio,
  nomeAcessivel,
  atual,
  registro = 'ancora',
  marca,
  aberto,
  aoAbrir,
  endereco,
  caixaRef,
  children,
}: {
  /** O nome deste nível. `null` = nível ainda não escolhido. */
  nome: string | null;
  /** Para onde o NOME leva. Ignorado quando não há nome. */
  para: string;
  /**
   * O que se lê quando não há nome — «Escolher cliente».
   *
   * Opcional porque nem todo degrau tem estado vazio: o da propriedade cai para
   * «Propriedade» antes de chegar aqui, e exigir dele um texto que nunca se lê
   * seria a peça combinando um estado que ela não tem.
   */
  rotuloVazio?: string;
  /** O nome acessível do galho. Contém o rótulo visível (WCAG 2.5.3). */
  nomeAcessivel: string;
  /** É o degrau mais fundo da trilha? Ele é o que fica em accent. */
  atual: boolean;
  /**
   * `ancora` é o degrau que sustenta o cabeçalho — 16px em Oswald, a única
   * âncora de leitura de uma faixa que é toda 10px em versalete. `escopo` é o
   * degrau seguinte, 13px: é o registro que a placa de escopo já tinha, e
   * repeti-lo aqui é o que faz o operador reconhecer a peça que mudou de lugar.
   * Dois nomes de 16px seguidos custariam ~260px contra 104,0 de folga.
   */
  registro?: 'ancora' | 'escopo';
  /** O ponto de estado, quando o nível tem um. Dado, não enfeite. */
  marca?: ReactNode;
  aberto: boolean;
  /** Recebe o próprio galho, para quem precisa ancorar uma lâmina nele. */
  aoAbrir: (galho: HTMLButtonElement) => void;
  /** `data-forma` da caixa — é por ele que a medição acha este degrau. */
  endereco: string;
  /** A caixa inteira, para o «clicou fora, fecha» de quem abriu. */
  caixaRef?: RefObject<HTMLDivElement | null>;
  /** O painel que o galho abre. Desenhado por quem sabe o que lista. */
  children?: ReactNode;
}) {
  const corpoDoNome = clsx(
    'max-w-[16ch] truncate font-display font-semibold uppercase leading-none tracking-tight',
    registro === 'ancora' ? 'text-base' : 'text-[13px]',
    atual ? 'text-accent' : 'text-dark/85',
  );

  return (
    <div
      ref={caixaRef}
      data-forma={endereco}
      className="relative hidden min-w-0 items-center gap-3 sm:flex"
    >
      {/* O separador é o da `Trilha`, e é ele que diz que este controle é o
          nível seguinte do caminho e não um botão avulso ao lado. */}
      <ChevronRight size={14} aria-hidden className="shrink-0 text-dark/40" />

      <div className="flex min-w-0 items-center gap-1.5">
        {nome ? (
          <Link
            to={para}
            title={nome}
            className={clsx(corpoDoNome, 'transition-colors hover:text-accent')}
          >
            {/* O ponto viaja DENTRO do link porque ele qualifica o nome, não o
                gesto de abrir a lista. */}
            {marca}
            {nome}
          </Link>
        ) : (
          /*
           * Sem nome não há ousadia: o degrau volta ao registro da trilha.
           * Grito em cima de um lugar vazio é o que faz um painel virar cartaz.
           *
           * `/85` E NÃO `/55`, E QUEM ACHOU FOI A CERCA.
           *
           * Este texto vinha de `SeletorDeCliente` a `dark/55`, e nunca tinha
           * sido auditado: a cerca de contraste sai das quatro raízes do
           * `<main>`, e o cabeçalho não é nenhuma delas. Ao virar peça de
           * `ui.tsx`, alcançada pelo `ClienteLayout`, ele entrou no grafo pela
           * primeira vez — e reprovou.
           *
           * Medido sobre `.vst-casca`: `dark/55` dá **3,12:1** no melhor caso e
           * **2,91:1** com tinta cheia rolando por baixo, num texto de 10px em
           * negrito, que não é «texto grande». `dark/85` dá **7,14:1** e
           * **6,13:1**. É o degrau que a barra do cliente já pagou e que esta
           * mesma cerca cobra da trilha, na afirmação «a trilha do cabeçalho
           * sobe mais, porque mora sobre vidro».
           */
          <span className="max-w-[16ch] truncate text-[10px] font-semibold uppercase tracking-[0.2em] text-dark/85">
            {rotuloVazio}
          </span>
        )}

        {/*
          O GALHO É UM BOTÃO SEPARADO DO NOME, E É ISSO A OBRA INTEIRA.

          Enquanto os dois eram o mesmo `<button>`, o nível não tinha endereço:
          clicar no cliente abria a lista de clientes em vez de ir ao cliente, e
          `header a[href="/c/:slug"]` respondia `null`. Um alvo, dois gestos —
          o que a `V3` reprova desde a leva 1.

          O alvo tem 24px de lado com o `p-0.5` e o glifo de 14: abaixo disso o
          galho vira um alvo de toque que só o rato acerta.
        */}
        <button
          type="button"
          onClick={(e) => aoAbrir(e.currentTarget)}
          aria-expanded={aberto}
          aria-haspopup="listbox"
          aria-label={nomeAcessivel}
          /*
           * `dark/60` E NÃO `dark/40`, E O NÚMERO É O MOTIVO.
           *
           * Enquanto o nome e o galho eram o mesmo botão, o glifo era enfeite:
           * quem carregava a afordância era a palavra. Separados, o galho passa
           * a ser um controle cuja ÚNICA marca visual é este chevron — e aí ele
           * cai sob o piso de 3:1 da WCAG para elemento não textual.
           *
           * Medido com `packages/core/src/design/cor.ts` sobre `.vst-casca`:
           * `dark/40` dá **2,18:1** no melhor caso e **2,09:1** com tinta cheia
           * rolando por baixo. `dark/60` dá **3,54:1** e **3,27:1**. É o mesmo
           * degrau que o ícone dos grupos da barra já usa, e pelo mesmo motivo.
           *
           * O `ChevronRight` que separa os degraus fica em `dark/40`: ele é
           * `aria-hidden` e não é alvo de nada — separador é enfeite, e enfeite
           * não tem piso de contraste.
           */
          className="-m-0.5 shrink-0 p-0.5 text-dark/60 transition-colors hover:text-accent"
        >
          <ChevronDown
            size={14}
            aria-hidden
            className={clsx('transition-transform', aberto && 'rotate-180')}
          />
        </button>
      </div>

      {children}
    </div>
  );
}

/**
 * A TRILHA — uma peça, três lugares.
 *
 * O painel tinha três sistemas de trilha, cada um com o seu `aria-label` e o
 * seu separador: o do cabeçalho (`AppShell`), a `Migalha` da área de membros e
 * a de `campanhas/EntityLevels`. Um operador que aprendeu a voltar numa não
 * tinha aprendido nas outras.
 *
 * O separador é `›` desenhado como `ChevronRight` e é `aria-hidden`: quem lê
 * por leitor de tela ouve a lista de links, e um chevron lido em voz alta entre
 * cada um é ruído. O nível SEM `para` é onde se está, e por isso ganha o aço —
 * é a mesma regra que o cabeçalho já aplicava à mão.
 */
export function Trilha({ niveis, className }: { niveis: NivelDaTrilha[]; className?: string }) {
  return (
    <nav
      data-forma="trilha"
      aria-label="Trilha de navegação"
      className={clsx(
        'flex min-w-0 items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em]',
        className,
      )}
    >
      {niveis.map((nivel, i) => {
        const largura =
          nivel.desde === 'sm'
            ? 'hidden sm:flex'
            : nivel.desde === 'xs'
              ? 'hidden min-[400px]:flex'
              : 'flex';
        const corpo = nivel.para ? (
          <Link
            to={nivel.para}
            title={nivel.title}
            className={clsx(
              /* `/85` e não `/75` como o resto: a trilha mora no cabeçalho, que
                 é `.vst-casca` — 88% de superfície com 20px de desfoque sobre o
                 que estiver rolando por baixo. Texto sobre vidro sobe de peso, e
                 `/85` é o degrau que a barra do cliente já pagou e o dono já
                 aprovou. A `/55` de ontem media 3,06:1 sobre o melhor caso. */
              'text-dark/85 transition-colors hover:text-accent',
              nivel.fixo ? 'min-w-max shrink-0' : 'truncate',
            )}
          >
            {nivel.rotulo}
          </Link>
        ) : (
          <span
            title={nivel.title}
            className={clsx('text-accent', nivel.fixo ? 'min-w-max shrink-0' : 'truncate')}
          >
            {nivel.rotulo}
          </span>
        );

        return (
          <span key={i} className={clsx('min-w-0 items-center gap-3', largura)}>
            {i > 0 && <ChevronRight size={14} aria-hidden className="shrink-0 text-dark/40" />}
            {corpo}
          </span>
        );
      })}
    </nav>
  );
}

/**
 * A explicação de uma tela, sem o custo de um cartão.
 *
 * Todo painel tem esse parágrafo: "é por aqui que a venda entra quando o
 * checkout está em outro domínio…". Ele é lido uma vez, na primeira visita, e
 * depois ocupa 120px para sempre — em cima do conteúdo, todos os dias.
 *
 * Aqui vira uma nota de duas linhas com fio à esquerda: mesmo texto, um terço
 * da altura, e sem a moldura de cartão que a fazia competir com os dados de
 * verdade logo abaixo. Ainda é a primeira coisa que se lê; deixou de ser a
 * maior coisa que se vê.
 */
export function PageNote({
  children,
  className,
  'data-forma': endereco,
}: {
  children: ReactNode;
  /** O endereço do bloco — ver o mesmo campo em `PageTitle` (decisão nº 82). */
  'data-forma'?: string;
  /**
   * Existe para quem compõe a nota dentro de outra peça e precisa desfazer a
   * margem de baixo. A `Testeira` do V-Social a punha ao LADO do título, e sem
   * esta prop ela teve de neutralizar o `mb-6` por fora, com um seletor de
   * filho — que funciona e é frágil: quem mudar o `mb` daqui não vê o outro.
   *
   * ── E ELA NÃO FUNCIONAVA, DESDE QUE NASCEU (30/08/2026) ──────────────────
   *
   * O `clsx` daqui só CONCATENA: `mb-0` de quem chama entrava na string e
   * perdia para o `mb-6` da peça, porque as duas regras existem na folha e a da
   * peça sai depois. A prop foi escrita **exatamente** para desfazer essa
   * margem e era a única coisa que ela não conseguia fazer — e sem erro nenhum,
   * que é o formato de defeito que esta casa mais paga.
   *
   * Quem o encontrou foi a cerca `a-peca-nao-engole-a-classe-do-chamador`, na
   * primeira vez que alguém tentou usar a prop para o que ela promete (a porta
   * do V-Members). O remédio é o que ela prescreve, e é um só: `mesclar` no
   * lugar de `clsx`, e a peça cede o eixo.
   */
  className?: string;
}) {
  return (
    <p
      data-forma={endereco}
      className={mesclar(
        'mb-6 max-w-3xl border-l-2 border-dark/15 pl-4 text-[13px] leading-relaxed text-dark/75',
        className,
      )}
    >
      {children}
    </p>
  );
}

/**
 * ── A FRESTA — O DADO FICA NA TELA, A EXPLICAÇÃO RECOLHE ────────────────────
 *
 * ## O que ela conserta, com o número
 *
 * Medido em `/c/:slug` em 26/08/2026, com o retrato de produção: **899px de
 * prosa** de ressalva numa página de 7.016 — a faixa da medição (106), o cartão
 * de moedas do faturamento (367), a chegada tardia (154), a cobertura de taxa
 * (39) e um SEGUNDO cartão de moedas dentro da mídia paga (233). Todas dizem
 * coisas verdadeiras. Nenhuma delas é lida duas vezes.
 *
 * A saída fácil seria apagar, e ela está errada: a frase da mistura de moeda
 * existe porque uma leva inteira desta casa foi gasta para que ela pudesse ser
 * honesta, e a da chegada tardia é a única coisa que explica por que o número
 * de sexta não é o de segunda.
 *
 * ## A regra que a governa, e ela é o que distingue esta peça de um acordeão
 *
 * **O que recolhe é a EXPLICAÇÃO; o que fica é o DADO.** Os subtotais por
 * moeda não são a explicação da mistura — eles *são* a verdade sob mistura
 * (`T3`), e por isso ficam na linha fechada, em corpo de dado. O que dobra é o
 * parágrafo que conta por que não dá para somá-los.
 *
 * É o teste que separa uma fresta honesta de um bloco escondido: **se a linha
 * fechada não afirma nada, você não compactou — você escondeu.** Quem passa o
 * olho lê «R$ 5.307,10 · € 148,00 · US$ 96,00 ≈ R$ 6.692,56» e já sabe tudo o
 * que precisa para não somar errado. O parágrafo é para a primeira vez.
 *
 * ## Por que não é `<details>`
 *
 * Porque `<summary>` é UM nó e esta peça tem dois papéis: uma fileira de dados
 * que precisa quebrar em várias linhas quando a coluna encolhe, e um controle
 * que precisa ficar na ponta. Dentro de `<summary>` a fileira inteira vira
 * área clicável, e clicar num subtotal para «abrir a explicação» é um gesto que
 * ninguém pediu. A casa tem as duas gramáticas — `Envios.tsx` usa `<details>`
 * para prosa pura, e é o uso certo dele.
 */
export interface ItemDaFresta {
  chave: string;
  /** O rótulo curto, em versalete — o nome da ressalva, não a ressalva. */
  rotulo: string;
  /** O DADO. Fica sempre visível, e nunca é uma frase explicativa. */
  resumo: ReactNode;
  /** A prosa, palavra por palavra como ela era antes de recolher. */
  corpo: ReactNode;
}

export function Fresta({
  itens,
  rotulo = 'Por que estes números são estes',
  className,
}: {
  itens: ItemDaFresta[];
  /** O que o controle promete. Diz o que se ganha ao abrir, nunca «detalhes». */
  rotulo?: string;
  className?: string;
}) {
  const [aberta, setAberta] = useState(false);
  const id = useId();
  if (itens.length === 0) return null;

  return (
    <div data-forma="ressalvas" className={mesclar('border-t border-dark/12 pt-4', className)}>
      <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
        {itens.map((item) => (
          <span key={item.chave} className="flex min-w-0 flex-wrap items-baseline gap-x-2">
            <MicroLabel>{item.rotulo}</MicroLabel>
            {/* `/85` e não `/75`: este é o registro de DADO, e ele tem de se
                distinguir do rótulo que o nomeia sem ganhar uma cor para isso. */}
            <span className="text-sm tabular-nums text-dark/85">{item.resumo}</span>
          </span>
        ))}

        <button
          type="button"
          onClick={() => setAberta((v) => !v)}
          aria-expanded={aberta}
          aria-controls={id}
          className="ml-auto inline-flex shrink-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-dark/75 transition-colors hover:text-accent"
        >
          {aberta ? 'Fechar' : rotulo}
          <ChevronDown
            size={13}
            aria-hidden
            className={clsx('transition-transform duration-200', aberta && 'rotate-180')}
          />
        </button>
      </div>

      {aberta && (
        <div id={id} className="mt-4 space-y-4">
          {itens.map((item) => (
            <div key={item.chave}>{item.corpo}</div>
          ))}
        </div>
      )}
    </div>
  );
}

type ButtonProps = ComponentPropsWithoutRef<'button'> & {
  variant?: 'solid' | 'outline' | 'ghost';
  loading?: boolean;
};

/**
 * ── O `type` PADRÃO É `button` — 29/08/2026, `§20.1` ────────────────────────
 *
 * A mesma doença da peça de cima, e nesta ela já mordia. `<button {...props}>`
 * sem `type` é `type="submit"` dentro de um `<form>`: no `<form onSubmit>` de
 * `FunnelLibrary.tsx:604` viviam três `<Button>` sem `type` — «Evento»,
 * «Fase» e **«Cancelar»** —, e os três chamavam `save.mutate()`. Acrescentar
 * uma fase salvava o funil; **desistir salvava o funil**.
 *
 * ## Por que padrão, e não cerca — a decisão foi do juiz e o motivo é o risco
 *
 * Aqui `submit` é uso legítimo e **maioritário**, ao contrário do segmentado, e
 * por isso a decisão não era óbvia. O que a desempatou foi ir conferir o risco
 * que mudar o padrão cria — **impedir** um envio — e achá-lo **vazio**: dos 93
 * `<form>` do painel, nenhum depende do padrão implícito. O único submetido de
 * fora (`FormularioDeBlog`, por `form={id}`) é acionado pelos dois chamadores
 * com `type="submit"` escrito à mão.
 *
 * E os dois modos de falha não são simétricos. Padrão `submit` falha **gravando
 * o que ninguém pediu, em silêncio**. Padrão `button` falha **não acontecendo
 * nada, no primeiro clique de quem escreveu a tela**. Entre um instrumento que
 * mente e um que se cala, esta casa já pagou para saber qual é o caro.
 *
 * **Os 80 `type="submit"` explícitos não se varrem.** Ficam redundantes e
 * corretos: escrito à mão, ele é declaração de intenção, e apagá-la para ganhar
 * simetria seria varredura com crachá de correção.
 */
/**
 * ── A RECEITA DO BOTÃO, PARA QUEM NAVEGA (31/08/2026, `D3` DA AUDITORIA Nº 5) ─
 *
 * O painel escreve `<Link to={...}><Button>Abrir</Button></Link>` em dezenas de
 * sítios, e isso é **`<button>` dentro de `<a>`** — HTML inválido, e duas
 * paradas de teclado onde há um destino só. Medido na lista de propriedades em
 * produção: 9 propriedades, **27 paradas de tabulação**, 8 cartões oferecendo o
 * mesmo `href` duas vezes.
 *
 * Um `Button` que aceitasse `to` seria a saída errada: a peça mudaria de
 * elemento conforme a prop, e quem lesse `<Button>` deixaria de saber o que sai
 * no DOM. O que se compartilha aqui é o **desenho**, não o elemento — a mesma
 * string de classes que o `Button` logo abaixo usa, aplicada num `<Link>` que já
 * é o elemento certo para ir a uma rota.
 *
 * Ela não introduz token nenhum: é a linha que já estava escrita dentro do
 * `Button`, agora com um nome.
 */
export function receitaDoBotao(variant: ButtonProps['variant'] = 'solid', className?: string) {
  return mesclar(
    'inline-flex items-center justify-center gap-2 px-6 py-3 text-[11px] font-semibold uppercase tracking-widest transition-all duration-300',
    'disabled:cursor-not-allowed disabled:opacity-40',
    variant === 'solid' && 'bg-dark text-white hover:bg-accent',
    variant === 'outline' &&
      'border border-dark/20 text-dark hover:border-accent hover:bg-accent hover:text-white',
    variant === 'ghost' && 'text-dark/75 hover:text-accent',
    className,
  );
}

export function Button({
  variant = 'solid',
  loading = false,
  className,
  children,
  disabled,
  type,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      type={type ?? 'button'}
      disabled={disabled || loading}
      className={receitaDoBotao(variant, className)}
    >
      {loading ? 'Aguarde…' : children}
    </button>
  );
}

/**
 * Rótulo + campo — e o campo nasce COM NOME (`X15`).
 *
 * O `<label>` envolvente já dava nome acessível por associação implícita, e a
 * auditoria da Rodada 5 leu cinco caixas de UTM "sem nome": ferramenta que
 * inspeciona atributo não vê associação implícita, e nem todo leitor de tela
 * antigo a resolve do mesmo jeito. Um `id` gerado aqui e um `htmlFor` explícito
 * custam nada e valem para TODO campo do painel que usa esta peça — que é o
 * lugar certo de consertar, pela mesma razão da caixa de marcar logo abaixo: o
 * conserto mora onde a peça mora.
 *
 * O `id` só é injetado quando o filho não traz um. Quem já se identifica
 * (campos com `id` próprio, formulários com âncora) continua mandando.
 */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  const gerado = useId();
  const filho = isValidElement(children) ? (children as ReactElement<{ id?: string }>) : null;
  const idDoFilho = filho?.props.id;
  const alvo = filho ? (idDoFilho ?? gerado) : undefined;
  const conteudo = filho && !idDoFilho ? cloneElement(filho, { id: gerado }) : children;

  return (
    <label className="block" {...(alvo ? { htmlFor: alvo } : {})}>
      <MicroLabel className="mb-2 block">{label}</MicroLabel>
      {conteudo}
      {hint && !error && <p className="mt-1.5 text-xs text-dark/75">{hint}</p>}
      {error && <p className="mt-1.5 text-xs text-error">{error}</p>}
    </label>
  );
}

/**
 * ── O CAMPO DESABILITADO DEIXA DE PARECER VIVO ──────────────────────────────
 *
 * Decisão nº 70 do `forma-juiz` (§29.5). `Input` e `Select` não tinham estilo de
 * `disabled` **nenhum**: um campo trancado desenhava-se exatamente como um campo
 * que aceita texto. Medido: **24 chamadas** com campo desabilitado no painel.
 *
 * **Não é token novo.** `disabled:cursor-not-allowed disabled:opacity-40` é a
 * classe que o `Button` desta mesma folha já usa (`:755`), palavra por palavra —
 * a `R1` fica intacta.
 *
 * O argumento que decidiu, e ele é do juiz: **a Lei B é indeliverável enquanto um
 * campo desabilitado parecer vivo.** «O bloco que impede traz o porquê e a saída»
 * pressupõe que o operador perceba que está impedido; sem isso ele lê o motivo ao
 * lado de um campo que parece aceitar texto, e a lei vira decoração.
 *
 * Custa zero pixel: opacidade não entra em leiaute, e nenhum número de nenhuma
 * tela muda de valor.
 */
export function Input({ className, ...props }: ComponentPropsWithoutRef<'input'>) {
  return (
    <input
      {...props}
      className={mesclar(
        'w-full border border-dark/15 bg-muted px-4 py-3 text-sm text-dark transition-colors duration-200',
        'placeholder:text-dark/45 focus:border-accent focus:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
    />
  );
}

/**
 * O campo de texto LONGO do painel.
 *
 * ## O defeito que ele conserta
 *
 * O motivo da devolução no portão do blog era um `<input type="text">` de uma
 * linha com `maxLength={600}` — e o próprio texto de exemplo não cabia nele. Um
 * campo de uma linha para 600 caracteres é o campo em que quem escreve menos
 * consegue reler o que escreveu, e este em particular vai **inteiro para o
 * prompt de um agente**: o texto que ninguém revê é o texto que manda o modelo
 * reescrever a matéria.
 *
 * Mesma pele do `Input` — mesma borda, mesmo fundo, mesmo foco. O que muda é a
 * altura e o `resize` vertical, que é a única direção em que crescer não estoura
 * a coluna. `field-sizing` ainda não é universal; `rows` continua sendo o
 * controle que todo navegador entende.
 */
export function Textarea({ className, rows = 3, ...props }: ComponentPropsWithoutRef<'textarea'>) {
  return (
    <textarea
      rows={rows}
      {...props}
      className={mesclar(
        'w-full resize-y border border-dark/15 bg-muted px-4 py-3 text-sm leading-relaxed text-dark transition-colors duration-200',
        'placeholder:text-dark/45 focus:border-accent focus:outline-none',
        className,
      )}
    />
  );
}

/**
 * A caixa de marcar do painel — e ela existe por causa de um alvo de 13 pixels.
 *
 * `<input type="checkbox">` cru é 13×13 no padrão de todo navegador de desktop.
 * A `V10` do `docs/blog-arquitetura.md` pede **24×24 no mínimo** (WCAG 2.5.8), e
 * a auditoria achou a caixa nua no editor de matéria — mas o culpado não é do
 * módulo do blog: é a ausência de um componente aqui. Consertar por dentro do
 * `.vst-blog` seria o módulo redefinindo um controle do painel, que é o que
 * `E2`/`F6` proíbem; o conserto certo é este, no lugar onde qualquer tela do
 * Vestigo o herda — o mesmo caminho pelo qual `V18` foi consertada.
 *
 * `accent-color` pinta o quadrado marcado com a cor da casa sem desenhar um
 * controle falso: o que o teclado e o leitor de tela recebem continua sendo um
 * checkbox nativo.
 */
export function Checkbox({
  label,
  hint,
  className,
  ...props
}: Omit<ComponentPropsWithoutRef<'input'>, 'type'> & { label: ReactNode; hint?: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 text-sm text-dark/75">
      <input
        {...props}
        type="checkbox"
        className={clsx(
          'h-6 w-6 shrink-0 cursor-pointer border border-dark/25 bg-muted',
          'accent-[color:var(--color-accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
          className,
        )}
      />
      <span className="min-w-0">
        {label}
        {hint && <span className="mt-0.5 block text-xs text-dark/75">{hint}</span>}
      </span>
    </label>
  );
}

/**
 * Select com a mesma casca do `Input`.
 *
 * Existe porque a string de classes do `<select>` estava copiada em meia dúzia
 * de páginas, cada cópia divergindo um pouco. A seta é desenhada por nós: a
 * nativa varia entre navegadores e destoa da borda reta do resto.
 *
 * ── DOIS ELEMENTOS, DOIS DONOS: `className` e `classNameDoInvolucro` ────────
 *
 * *derrubada por ordem do dono de 13/09/2026 (agente uiux): a peça deixou de
 * ter um único destino de classe, porque um único destino era a causa-raiz
 * escrita de dois defeitos medidos.*
 *
 * `Select` devolve **dois** elementos — o `<span>` invólucro (que é o que a
 * linha de fora vê: largura, margem, alinhamento) e o `<select>` (que é o que
 * o olho vê: fundo, borda, altura, tipografia). Até aqui havia **um** lugar
 * para pôr classe, e ele era o de dentro. Quem quisesse mexer no de fora não
 * tinha como, e a classe que escrevia caía num elemento onde ou não fazia nada
 * ou fazia a coisa errada. `pele.tsx` do V-Forms registrou isto por escrito, em
 * duas passagens, como dívida de `ui.tsx`:
 *
 * - **«nenhum chamador consegue desfazer o `w-full`»** — num `flex flex-wrap` o
 *   invólucro a 100% quebra sozinho para uma linha só sua, e o `w-auto` que a
 *   aba passava ia para o `<select>` de dentro sem efeito nenhum sobre a linha.
 *   Quatro controlos de recorte esticados por **1 276 px** cada;
 * - **«a seta a flutuar 30 px à direita da pílula»** — a seta estava
 *   `absolute right-3` contra o INVÓLUCRO, logo ela media a caixa e não o
 *   campo. Um `<select>` mais estreito que o invólucro (`w-40`, `w-48`, `w-52`
 *   — quatro sítios hoje) largava a seta no vazio, como se fosse de outro
 *   controlo.
 *
 * O conserto tem duas metades, e elas são deliberadamente diferentes:
 *
 * 1. **`classNameDoInvolucro` é prop NOVA, e `className` NÃO mudou de destino.**
 *    Há 40 chamadores com `className` hoje, em 151 usos; ~30 deles escrevem
 *    pele do campo (`py-2`, `text-xs`, `rounded-full`, `border-error`) e
 *    contam com o destino atual. Virar o destino calaria os 30 e consertaria os
 *    10 — o `className` continua no `<select>`, e quem precisa do de fora pede
 *    o de fora pelo nome. Nome errado não é descartado em silêncio: é erro de
 *    tipo, porque a prop é declarada.
 * 2. **A seta deixou de ser `absolute` e passou a ser IRMÃ EM LINHA do campo**,
 *    puxada para dentro por margem negativa do seu próprio tamanho. Isto não é
 *    prop nenhuma: é o invariante de que **a seta pertence ao campo**, e ele
 *    passa a valer para os 151 usos sem que ninguém os edite — incluindo os
 *    quatro que hoje a deixam a flutuar. A conta é fechada e some no layout:
 *    `-ml-3.5` (−14 px) contra os 14 px do ícone dá largura ocupada **zero**,
 *    e o `-translate-x-3` repõe, sem custo de layout, os mesmos 12 px da borda
 *    que o `right-3` dava. Campo cheio: pixel idêntico ao de antes. Campo
 *    estreito: a seta vai com ele.
 *
 * O que continua do invólucro, e por isso continua a pedir
 * `classNameDoInvolucro`: **margem e alinhamento vertical**. `mt-1.5` no
 * `<select>` faz o invólucro crescer 6 px em cima e a seta centrar-se 3 px
 * abaixo do campo; `self-end` no `<select>` não alcança a linha de fora e não
 * faz nada. Os dois querem dizer o invólucro.
 */
export function Select({
  className,
  classNameDoInvolucro,
  children,
  ...props
}: ComponentPropsWithoutRef<'select'> & {
  /**
   * A classe do `<span>` invólucro — largura, margem, alinhamento na linha.
   *
   * É aqui que morre o `w-full` de fábrica: `classNameDoInvolucro="w-auto"` ou
   * `"w-[9.5rem]"` faz o controlo declarar quanto ocupa, e `mesclar` cede o
   * eixo em vez de empatar por ordem da folha.
   */
  classNameDoInvolucro?: string;
}) {
  return (
    <span className={mesclar('relative inline-flex w-full items-center', classNameDoInvolucro)}>
      {/* Ver o cabeçalho do `Input`: a mesma decisão nº 70, a mesma classe do
          `Button`, e nenhum token novo. */}
      <select
        {...props}
        className={mesclar(
          'peer w-full appearance-none border border-dark/15 bg-muted py-3 pl-4 pr-9 text-sm text-dark',
          'transition-colors duration-200 focus:border-accent focus:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-40',
          className,
        )}
      >
        {children}
      </select>
      {/*
       * A seta apaga **com** o campo, e é por isso que há um `peer` aqui.
       *
       * Ela é irmã do `<select>` e não filha dele, então a opacidade do campo não
       * a alcança: sem esta linha, um campo trancado ficava a 40% com uma seta
       * nítida por cima — a única coisa acesa num controlo apagado, que se lê
       * como defeito de desenho e não como estado.
       *
       * `peer`/`peer-disabled` é mecanismo do Tailwind, não vocabulário desta
       * casa: não há cor, raio, sombra nem duração nova. O valor é o mesmo 40%.
       *
       * E ela é irmã **em linha**, não `absolute`: ver o cabeçalho. `-ml-3.5`
       * são exactamente os 14 px do `size={14}`, logo ela ocupa largura zero e
       * nenhum campo encolhe; `-translate-x-3` é pintura e não layout, e repõe
       * os 12 px que o antigo `right-3` dava. A diferença está em CONTRA O QUE
       * os 12 px se medem: agora contra a borda do campo, antes contra a borda
       * da caixa.
       */}
      <ChevronDown
        size={14}
        aria-hidden
        className="pointer-events-none -ml-3.5 shrink-0 -translate-x-3 text-dark/55 peer-disabled:opacity-40"
      />
    </span>
  );
}

export interface TabItem<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
}

/**
 * Abas de conteúdo. Segue o vocabulário da navegação do cliente (sublinhado
 * em `accent` no item ativo) para o usuário reconhecer o gesto de uma tela
 * para a outra, em vez de aprender um controle novo.
 *
 * A aba INATIVA era `dark/55` — 3,13:1 sobre o papel, a faixa em que todo
 * sistema operacional desenha um controle desabilitado. E aba inativa não é
 * desabilitada: ela é o destino do próximo clique, e o rótulo dela é a única
 * coisa que diz o que há do outro lado. O que separa ativo de inativo aqui é o
 * sublinhado em `accent` e a tinta cheia no hover, não a legibilidade.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  className,
}: {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div
      className={clsx('flex flex-wrap gap-x-7 border-b border-dark/10', className)}
      role="tablist"
    >
      {items.map((item) => {
        const active = item.value === value;
        const Icon = item.icon;
        return (
          <button
            key={item.value}
            /* `type="button"` porque um `<button>` sem `type` dentro de `<form>`
               e `submit`: gesto trocado que grava. O `SegmentedControl`, umas
               dezenas de linhas abaixo, ja fecha esta porta ha 84 usos, e esta
               peca ficou de fora. Alcance medido hoje: ZERO -- dos 15 arquivos
               que usam `<Tabs`, 6 tambem tem `<form` e NENHUM tem o `<Tabs`
               aninhado dentro dele (§20.4b). Defeito latente, e latente nao e
               sao. */
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={clsx(
              'flex items-center gap-1.5 border-b-2 px-0.5 pb-3 pt-1 text-xs font-bold uppercase tracking-[0.12em] transition-colors',
              active
                ? 'border-accent text-accent'
                : 'border-transparent text-dark/75 hover:text-dark',
            )}
          >
            {Icon && <Icon size={14} />}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Escolha única entre poucas opções, no formato de blocos colados por fio de
 * cabelo — o mesmo desenho dos seletores de período que já existem nas páginas
 * de relatório.
 */
/**
 * `compacto` existe por uma razão de largura, não de gosto.
 *
 * Num painel lateral estreito, três opções com o espaçamento normal somam mais
 * que a coluna — e o resultado não é um controle apertado, é uma **barra de
 * rolagem horizontal** na tela inteira, que é o defeito mais barato de produzir
 * e o mais difícil de perceber revisando código. No modo compacto as opções
 * dividem a largura disponível e nunca a estouram.
 */
/**
 * ── O `type`, E ELE VALE PELOS 84 USOS — 29/08/2026, `§19.1` ────────────────
 *
 * Esta peça desenhava `<button>` **sem `type`**. Em HTML, um `<button>` sem
 * `type` dentro de um `<form>` é `type="submit"` — então escolher uma opção
 * **submetia o formulário em volta**. Não é botão morto: é gesto trocado que
 * **grava**.
 *
 * O V-Rotas trouxe o sintoma («escolher onde a rota nasce submetia o
 * cadastro») e o juiz reconferiu o alcance no primário: **84 usos em 32
 * arquivos**, três deles dentro de `<form onSubmit>` em produção —
 * `ConsentBannerForm` (salva o banner), `Contatos:1729` (aplica os filtros) e
 * `Contatos:2919`, onde escolher «Meta» ou «Google» **cria o segmento ou o
 * público**. Um clique que o operador leu como *escolher* e o banco leu como
 * *criar*.
 *
 * É a doença nomeada desta casa — instrumento que mente em silêncio: quem
 * medisse leria a recusa do servidor e procuraria o defeito na rota, nunca no
 * controle. Por isso o conserto é **na peça** e nenhum dos três sítios se toca:
 * uma palavra aqui cobre os 84 usos e todos os que ainda não existem.
 *
 * `type` continua repassável porque existe o caso legítimo — um segmentado que
 * É o gesto de envio do formulário. O que ele deixa de ser é o **padrão
 * calado**.
 */
export function SegmentedControl<T extends string>({
  items,
  value,
  onChange,
  className,
  compacto,
  type,
}: {
  items: readonly { value: T; label: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  compacto?: boolean;
  type?: 'button' | 'submit' | 'reset';
}) {
  return (
    <div className={clsx('flex gap-px bg-dark/10', compacto && 'w-full', className)}>
      {items.map((item) => (
        <button
          key={item.value}
          type={type ?? 'button'}
          onClick={() => onChange(item.value)}
          aria-pressed={item.value === value}
          className={clsx(
            'font-semibold uppercase transition-colors',
            compacto
              ? 'min-w-0 flex-1 truncate px-1.5 py-1.5 text-[10px] tracking-[0.06em]'
              : 'px-4 py-2 text-xs tracking-[0.12em]',
            item.value === value
              ? 'bg-dark text-surface'
              : 'bg-surface text-dark/75 hover:text-dark',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A caixa de conteúdo — papel sobre a mesa.
 *
 * ## A sombra, e por que ela é dura
 *
 * A caixa não se distinguia do fundo: `bg-surface` sobre um `body` também
 * `surface`, separados por um fio a 10%. Em tela densa isso vira uma parede de
 * fios sem hierarquia — não dá para dizer onde uma caixa começa.
 *
 * O fundo da página recuou (ver `--color-desk`), e isso sozinho já resolve a
 * maior parte. A sombra entra para o resto: caixa dentro de caixa, e a sensação
 * de plano que o fundo sozinho não dá.
 *
 * **Sem desfoque, deslocada em 2px, na tinta do sistema.** Uma sombra suave —
 * o reflexo de qualquer biblioteca — traria a linguagem de elevação material
 * para um sistema que não tem canto arredondado, não tem gradiente e desenha
 * tudo com fio de 1px. Ela seria a única coisa macia na tela. Deslocamento duro
 * é a linguagem de registro de impressão: a mesma chapa, impressa duas vezes
 * com um milímetro de diferença. Combina com o resto porque é feita da mesma
 * matéria — aresta.
 *
 * Fica a 5% porque o objetivo é separar planos, não anunciar profundidade. Se
 * ela for perceptível como sombra, está forte demais.
 *
 * ## O CANTO — 26/08/2026, e ele estava a ZERO, não a 3px
 *
 * O produto tem `--radius-vst: 3px` desde o começo, com um comentário que
 * promete «aparece na borda de uma caixa grande». Esta peça nunca o importou:
 * o `className` era `border border-dark/12` e mais nada. **Os 319 `<Card>` de
 * 109 arquivos estavam em ângulo reto**, e o dono foi quem viu.
 *
 * Agora ela usa `rounded-vst-md` — o raio da PLACA, 10px, o mesmo valor que a
 * lâmina do editor já usava. A sombra continua dura: canto arredondado com
 * sombra macia é o cartão genérico de que este sistema fugiu; com sombra de
 * registro de impressão, não é de ninguém.
 */
export function Card({
  children,
  className,
  padded = true,
  tom = 'neutro',
  'data-forma': endereco,
  'data-gesto-primario': gestoPrimario,
  'data-vigiado': vigiado,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
  /**
   * Tinge o cartão pelo assunto que ele carrega — ver `fundoDoCartao`.
   * `neutro` é o padrão e cobre a maioria das telas.
   */
  tom?: TomDeCartao;
  /**
   * O endereço que a régua do V-Forma mede (`G2`), colado NA PEÇA.
   *
   * Existe porque a alternativa é embrulhar o cartão numa `<div>` só para
   * pendurar o atributo — e foi assim que a cerca da série de eventos acabou
   * medindo um invólucro de dois cartões em vez da curva que ela julga. Quando
   * o leiaute reagrupa, o invólucro some e a cerca some com ele; um endereço
   * colado na peça sobrevive.
   */
  'data-forma'?: string;
  /**
   * A marca do gesto primário, e ela existe pelo mesmo motivo do `data-forma`
   * — com um agravante.
   *
   * Antes, `Card` encaminhava `data-forma` e **engolia todo o resto em
   * silêncio**: quem escrevesse `<Card data-gesto-primario>` via o atributo
   * sumir do DOM sem aviso, e a cerca que o procura passava **medindo `null`**.
   * Uma medição que erra dói mais que uma que falta: a que falta se vê.
   * Aconteceu na aba Impacto do V-Social.
   */
  'data-gesto-primario'?: boolean;
  /**
   * O id do perfil vigiado que este cartão desenha — a âncora das cercas do
   * V-Social.
   *
   * ⚠ **Ele existe pela mesma razão que `data-forma`, e por um defeito medido.**
   * As cercas daquela área descobriam o cartão por `[data-lista="vigiados"] > *`
   * — os filhos DIRECTOS da lista. No dia em que os perfis passaram a ser
   * agrupados por marca, os filhos directos viraram secções e nove cercas
   * ficaram vermelhas a dizer «a lista não desenhou cartão nenhum» sobre uma
   * tela que desenhava cinco.
   *
   * Descobrir por profundidade envelhece como descobrir por posição. Um nome
   * colado na peça sobrevive ao reagrupamento — e `Card` engole em silêncio
   * qualquer atributo que não esteja nesta lista, que é o agravante que o
   * `data-gesto-primario` já documenta duas linhas acima.
   */
  'data-vigiado'?: string;
}) {
  const fundo = fundoDoCartao(tom);
  return (
    <div
      data-forma={endereco}
      data-gesto-primario={gestoPrimario ? '' : undefined}
      data-vigiado={vigiado}
      className={mesclar(
        'rounded-vst-md border border-dark/12',
        tom === 'neutro' && 'bg-surface',
        padded && 'p-5',
        className,
      )}
      style={{
        boxShadow: '2px 2px 0 rgba(45, 50, 47, 0.05)',
        ...(fundo ? { backgroundColor: fundo } : {}),
      }}
    >
      {children}
    </div>
  );
}

/** Grade de métricas separada por 1px, como na seção de stats da referência. */
export function StatGrid({ children, columns = 4 }: { children: ReactNode; columns?: number }) {
  return (
    <div
      className="grid gap-px bg-dark/10"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {children}
    </div>
  );
}

export function Stat({
  label,
  value,
  tone,
  hint,
  onClick,
}: {
  label: string;
  value: ReactNode;
  tone?: Tone;
  /** Uma linha de contexto sob o rótulo — o que o número compara. */
  hint?: ReactNode;
  /**
   * Torna a célula clicável, para o número levar ao que ele conta.
   *
   * Existe porque "1 ALERTAS" era um beco: o painel avisava que havia um
   * problema e não dizia qual, nem onde. Um número que acusa precisa de porta.
   */
  onClick?: () => void;
}) {
  const Elemento = onClick ? 'button' : 'div';
  return (
    <Elemento
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={clsx(
        'bg-surface p-6 text-center',
        onClick && 'w-full transition-colors duration-200 hover:bg-muted',
      )}
    >
      <p
        className={clsx(
          'font-display text-3xl font-semibold tracking-tighter md:text-4xl',
          tone === 'ok' && 'text-ok',
          tone === 'warn' && 'text-warn',
          tone === 'error' && 'text-error',
          !tone && 'text-dark',
        )}
      >
        {value}
      </p>
      {/* Rótulo e dica subiram de `/65` e `/50` para o piso derivado: numa célula
          cujo número é `text-3xl`, a hierarquia já está no tamanho, e apagar a
          legenda só faz o operador não saber o que o número conta. */}
      <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-dark/75">{label}</p>
      {hint && <p className="mt-1 text-xs text-dark/75">{hint}</p>}
    </Elemento>
  );
}

/**
 * Cartão de métrica-chave — o bloco que responde "como está indo" antes de
 * qualquer tabela.
 *
 * Diferente do `Stat` (célula de uma grade densa), este é uma peça isolada com
 * borda própria, feita para ser lida de longe: rótulo legível em cima, número
 * grande embaixo, e uma linha de apoio opcional. `emphasis` inverte o cartão
 * para o número mais importante da tela — um por tela, nunca dois.
 */
export function MetricCard({
  label,
  value,
  hint,
  tone,
  icon: Icon,
  emphasis = false,
  denso = false,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  icon?: LucideIcon;
  emphasis?: boolean;
  /**
   * O mesmo cartão sem ar — a faixa que não é o assunto da tela.
   *
   * Medido: o cartão padrão dá 104 px de altura (20+20 de `p-5`, 16 de rótulo,
   * 12 de respiro, 36 do número), e a régua do V-Forma dá **72 px** para a
   * banda inteira da porta. A porta não tem 104 px para gastar num número que
   * ela mostra só para dizer que está tudo no lugar — o assunto dela é a lista
   * de clientes logo acima.
   *
   * O que sai é AR e ícone, nunca anatomia: rótulo em versalete em cima, número
   * em display embaixo, tinta por opacidade. Um segundo desenho de cartão de
   * métrica é o que a `G5` proíbe; isto é o mesmo cartão com menos folga.
   */
  denso?: boolean;
}) {
  return (
    <div
      className={clsx(
        /* O raio da placa, como o `Card` — e ele pode vir aqui porque o
           `MetricGrid` separa os cartões com `gap-3`. O `Stat` logo acima NÃO o
           recebe: ele mora num `StatGrid` de `gap-px`, onde o fio de cabelo
           entre as células é a junta, e uma célula arredondada abre quatro
           buracos em cada cruzamento. A peça que encosta na vizinha não
           arredonda. */
        'rounded-vst-md border',
        denso ? 'p-3' : 'p-5',
        emphasis ? 'border-dark bg-dark text-surface' : 'border-dark/15 bg-surface',
      )}
    >
      <div className="flex items-center gap-2">
        {Icon && !denso && (
          <Icon size={14} className={emphasis ? 'text-surface/70' : 'text-dark/50'} aria-hidden />
        )}
        <span
          className={clsx(
            'text-[11px] font-bold uppercase tracking-[0.16em]',
            emphasis ? 'text-surface/75' : 'text-dark/75',
          )}
        >
          {label}
        </span>
      </div>

      <p
        className={clsx(
          'font-display font-semibold leading-none tracking-tighter tabular-nums',
          denso ? 'mt-1 text-2xl' : 'mt-3 text-4xl',
          emphasis
            ? 'text-surface'
            : tone === 'ok'
              ? 'text-ok'
              : tone === 'warn'
                ? 'text-warn'
                : tone === 'error'
                  ? 'text-error'
                  : 'text-dark',
        )}
      >
        {value}
      </p>

      {hint && (
        <p className={clsx('mt-2 text-xs', emphasis ? 'text-surface/60' : 'text-dark/75')}>
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * Grade responsiva de `MetricCard`, colada por fio de cabelo como o `StatGrid`.
 *
 * `colunas` troca a classe inteira em vez de acrescentar uma: duas utilitárias
 * do mesmo eixo (`xl:grid-cols-3` e `xl:grid-cols-4`) têm a mesma
 * especificidade, e quem vence é a ordem da folha — não a ordem em que se
 * escreveu no `className`. Uma grade de três cartões desenhada em quatro
 * colunas deixa um quarto de linha vazio à direita, e o vazio lê como bloco que
 * não carregou.
 *
 * ## Por que existe o `6`, e por que ele para em `lg:grid-cols-3`
 *
 * A banda da porta precisou de seis cartões sem ganhar altura: seis numa
 * fileira só cabem a partir de `xl`. Abaixo disso a mesma grade cai para três
 * colunas e duas fileiras — **153px medidos a 1.024**, dentro dos 160 que a
 * régua dá ali. Seis numa fileira a 1.024 daria 150px por cartão, e o rótulo em
 * versalete quebraria em duas linhas: a altura voltaria pela porta dos fundos.
 *
 * Uma coluna de grade **não é token** — a cerca proíbe cor, raio, sombra,
 * curva, duração, família e biblioteca, e nenhuma delas é uma coluna.
 */
export function MetricGrid({
  children,
  className,
  colunas = 4,
  'data-forma': endereco,
  'data-gesto-primario': gestoPrimario,
}: {
  children: ReactNode;
  className?: string;
  /**
   * O gesto primário da tela, encaminhado pelo MESMO motivo do `data-forma` — e
   * pelo defeito que o `Card` já registra: sem a prop declarada, quem escreve
   * `<MetricGrid data-gesto-primario>` vê o atributo **sumir do DOM em
   * silêncio**, e a medição que o procura responde `-1` como se a tela não
   * tivesse gesto nenhum. Foi exatamente o que aconteceu na aba Números do
   * V-Player, e quem achou foi a cerca das cinco telas.
   */
  'data-gesto-primario'?: boolean;
  /**
   * O endereço, encaminhado pelo mesmo motivo do `Card` — e para desfazer um
   * custo que já era hábito: sem esta prop, **toda banda de métrica embrulhava
   * a grade numa `<div>` só para pendurar o atributo**. O invólucro não
   * desenha nada, some quando o leiaute reagrupa, e leva a cerca junto.
   */
  'data-forma'?: string;
  /**
   * `5` entrou com o registro de resultado do bloco de Faturamento: são cinco
   * cartões — receita, investido, dois ROAS e lucro — e em quatro colunas o
   * quinto cai sozinho numa segunda fileira, que é o desenho que o split
   * existe para evitar. Contagem de coluna de grade **não é token** (§12.2 da
   * régua: a cerca proíbe cor, raio, sombra, curva, duração, família e
   * biblioteca, e nenhuma delas é uma coluna).
   */
  /**
   * `2` entrou com a banda de Logs, que desceu para a coluna de 20rem ao lado
   * da tabela (`D26`). Ali `sm:grid-cols-2` + `lg:grid-cols-3` daria **três
   * cartões em 320px** — 100px cada, e o rótulo em versalete quebra em duas
   * linhas: a altura voltaria pela porta dos fundos, que é o mesmo defeito que
   * o `6` já registra acima. Com `2` a grade fica em duas colunas em toda
   * largura, e é a única contagem que não muda no `lg`.
   */
  colunas?: 2 | 3 | 4 | 5 | 6;
}) {
  return (
    <div
      data-forma={endereco}
      data-gesto-primario={gestoPrimario ? '' : undefined}
      className={clsx(
        'grid gap-3 sm:grid-cols-2',
        colunas === 2
          ? /* nada no `lg`: duas colunas em toda largura — ver o docblock */ ''
          : colunas === 3
            ? 'lg:grid-cols-3'
            : colunas === 5
              ? 'lg:grid-cols-3 xl:grid-cols-5'
              : colunas === 6
                ? 'lg:grid-cols-3 xl:grid-cols-6'
                : 'lg:grid-cols-3 xl:grid-cols-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

export type Tone = 'ok' | 'warn' | 'error' | 'info' | 'neutral';

/**
 * ── A LÂMPADA — o estado dito por FORMA, antes de qualquer cor ──────────────
 *
 * Acesa é cheia; apagada é **oca**. O argumento inteiro está no `index.css`, no
 * bloco `.mb-lampada`: dois pontos de 7px em oliva e cinza, a três metros, são o
 * mesmo ponto — a forma diz antes da cor, e continua dizendo para quem não
 * distingue as duas.
 *
 * ## Por que ela mudou de casa em 30/08/2026
 *
 * Ela nasceu em `pages/cliente/areas/pecas.tsx`, e as duas variáveis que ela usa
 * (`--mb-noar`, `--mb-rascunho`) **já tinham sido promovidas ao `:root`** com o
 * recado escrito: *«a `Lampada` é peça, e peça não pode depender do escopo de um
 * módulo»*. O componente ficou atrás. A consequência apareceu quando a lista de
 * propriedades passou a precisar dele: importar de `areas/pecas` arrasta **1.138
 * linhas do V-Members** para o pedaço da tela de propriedades, por duas linhas
 * de JSX. Uma peça que só se alcança pagando o módulo inteiro é uma peça órfã
 * com outro nome.
 *
 * `pecas.tsx` reexporta daqui, então nenhum dos oito chamadores do Member muda.
 * A classe continua com o prefixo `mb-` porque renomeá-la mexeria em todos eles
 * sem mudar um pixel — o prefixo é dívida de nome, não de escopo.
 */
export function Lampada({ acesa, titulo }: { acesa: boolean; titulo?: string }) {
  return <span className="mb-lampada" data-acesa={acesa ? '1' : '0'} title={titulo} />;
}

/**
 * A etiqueta de estado.
 *
 * As quatro variantes de cor se leem porque a tríade de estado escureceu na
 * paleta do painel — ver o bloco `--color-ok`/`--color-warn`/`--color-error` em
 * `index.css`, com a tabela de medições. Aqui só sobrou o caso que não tem cor:
 *
 * **`neutral` era `text-dark/65` e reprovava.** Sobre o próprio `bg-dark/5` ele
 * media 3,91:1 no papel, 3,78:1 na mesa e **3,50:1 na prancheta** — abaixo do
 * piso de 4,5:1 nas três, e é a variante de `Rascunho` e `Arquivada`, os dois
 * estados que mais aparecem numa lista de matérias. A 85% da tinta mede 6,74 ·
 * 6,38 · 5,65, e 85% é o degrau que a escala da casa já tem.
 *
 * O peso e o corpo continuam onde estavam (9px, `font-semibold`): eles são um
 * dos quatro registros tipográficos do sistema, e o defeito medido era de cor.
 */
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={clsx(
        'inline-block border px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.2em]',
        tone === 'ok' && 'border-ok/30 bg-ok/10 text-ok',
        tone === 'warn' && 'border-warn/30 bg-warn/10 text-warn',
        tone === 'error' && 'border-error/30 bg-error/10 text-error',
        tone === 'info' && 'border-accent/30 bg-accent/10 text-accent',
        tone === 'neutral' && 'border-dark/20 bg-dark/5 text-dark/85',
      )}
    >
      {children}
    </span>
  );
}

/**
 * Uma saída de verdade.
 *
 * `Exclude` tira `null`, `undefined` e `boolean` do `ReactNode` porque os três
 * são as formas de escrever «esqueci» que passam por engano: `action={null}`,
 * `action={undefined}` e — a mais comum — `action={temPermissao && <Button/>}`,
 * que rende `false` e some com a porta justamente para quem menos pode.
 */
type SaidaDoVazio = Exclude<ReactNode, boolean | null | undefined>;

/**
 * ── O VAZIO TEM PORTA, E A FALTA DELA É DECISÃO ESCRITA ─────────────────────
 *
 * `action` era opcional, e o produto respondeu como se responde a um opcional:
 * dos **86** `<EmptyState>` do painel, **62 não tinham saída** — 60 sem a prop,
 * em 53 arquivos, e mais 2 que a passavam resolvendo para `undefined`
 * (`VisaoGlobal.tsx`, `organico/Impacto.tsx`). «Nenhum funil salvo», «Nada saiu
 * ainda», «Nenhuma chave ainda»: a tela informa que não há nada e deixa a
 * pessoa procurando por onde começar.
 *
 * Enquanto isso o `Vazio` do V-Send e o do Member já tinham `acao`
 * **obrigatória por tipo**. Não faltava disciplina lá: faltava contrato aqui.
 *
 * ## E «obrigatória por tipo» não era obrigatória por VALOR
 *
 * Os dois escreviam `acao: ReactNode` — e `ReactNode` **inclui `null`**. Sob
 * esse tipo, `acao={null}` compila, conta como «tem saída» em qualquer varredura
 * por presença de prop, e desenha uma `<div>` vazia. Eram 3 becos assim nos 11
 * sítios do V-Send e 5 nos 37 do Member (`areas/Aluno.tsx` ×4,
 * `areas/Idiomas.tsx`), mais dois que punham TEXTO onde vai o gesto. É por isso
 * que aqui o tipo é `SaidaDoVazio`, e não `ReactNode`.
 *
 * A obra é subir o contrato, não a forma — o desenho aqui não mudou uma classe.
 * O que mudou é que agora **o TypeScript é a cerca**, e ela é a melhor espécie:
 * não dá para esquecer, porque não compila.
 *
 * ## `naoHaAcao` é a válvula, e ela custa uma frase
 *
 * Há vazio sem porta legítimo — o convite que se emite noutra aba, o pacote que
 * a Vestigo ainda não pôs à venda. Para esses o tipo pede uma escolha
 * **nomeada**, nunca um `undefined`: `undefined` é esquecimento, `naoHaAcao` é
 * decisão. E ela não é de graça — o valor **é o motivo**, obrigatório e em
 * prosa, porque um vazio que diz «não há e não há o que fazer» sem dizer por
 * quê é a tela desistindo do operador.
 *
 * O motivo aparece onde o botão apareceria, atrás de um fio: dá para ver de
 * longe, varrendo o produto, a diferença entre um vazio que decidiu não ter
 * porta e um que teria esquecido. O orçamento de `naoHaAcao` está escrito e é
 * medido em `a-peca-orfa-se-conta.test.ts`.
 */
type PropsDoVazio = {
  title: string;
  description: string;
} & ({ action: SaidaDoVazio; naoHaAcao?: never } | { action?: never; naoHaAcao: string });

export function EmptyState(props: PropsDoVazio) {
  return (
    <div className="rounded-vst-md border border-dashed border-dark/15 px-6 py-16 text-center">
      <h3 className="font-display text-xl font-semibold uppercase tracking-tight text-dark/85">
        {props.title}
      </h3>
      <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-dark/75">
        {props.description}
      </p>
      {/*
        O motivo é `dark/75` e não `dark/55`, e não foi escolha de gosto:
        `a-tinta-nao-volta-a-desabilitada.test.ts` deriva o piso da tinta escura
        dos papéis reais desta casa e ele é **75**. A 55 esta linha reprovava —
        e reprovava justamente onde ela mais precisa ser lida, que é o vazio de
        quem não tem o que clicar.

        A subordinação, então, é de CORPO e de FIO, não de tinta: 13px contra os
        14 da descrição, atrás de um fio de cabelo, no lugar exato onde o botão
        estaria. Dá para varrer o produto e ver de longe a diferença entre um
        vazio que decidiu não ter porta e um que teria esquecido.
      */}
      {props.naoHaAcao ? (
        <p className="mx-auto mt-8 max-w-md border-t border-dark/10 pt-4 text-[13px] leading-relaxed text-dark/75">
          {props.naoHaAcao}
        </p>
      ) : (
        <div className="mt-8 flex justify-center">{props.action}</div>
      )}
    </div>
  );
}

export function ErrorNotice({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-vst-md border border-error/30 bg-error/5 px-4 py-3 text-sm text-error">
      {children}
    </div>
  );
}

/**
 * Copiar para a área de transferência.
 *
 * Estava duplicado em oito arquivos, cada cópia com o próprio `useState` e o
 * próprio tempo de "Copiado". Um lugar só: a ação tem o mesmo nome e o mesmo
 * comportamento em toda a aplicação, que é como alguém aprende a usá-la.
 */
export function CopyButton({
  text,
  label = 'Copiar',
  /** Versão miúda, para cabeçalho denso (o rótulo de um bloco de JSON). */
  compact = false,
  /**
   * O que foi copiado, para o anúncio — «Link do convite copiado».
   *
   * Sem ele o anúncio é «Copiado» sozinho, que é o certo quando há um botão só
   * na tela e ambíguo quando há três.
   */
  oQueFoiCopiado,
}: {
  text: string;
  label?: string;
  compact?: boolean;
  oQueFoiCopiado?: string;
}) {
  const [copied, setCopied] = useState(false);

  function copy() {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  /*
   * ── A CÓPIA CONFIRMA, E CONFIRMA TAMBÉM PARA QUEM NÃO VÊ ────────────────────
   *
   * O rótulo virava «Copiado» e mais nada acontecia. Trocar o texto de um botão
   * **que está com o foco** não é anunciado de forma confiável por leitor de
   * tela nenhum: o gesto ficava sem retorno para quem depende dele, e o segundo
   * clique — o de quem não sabe se o primeiro funcionou — é o sintoma.
   *
   * A região viva é irmã do botão e fica vazia até haver o que dizer: uma região
   * que já nasce com texto anuncia na montagem da tela, e aí quinze botões de
   * copiar falam todos ao mesmo tempo quando a página abre.
   */
  const anuncio = copied ? `${oQueFoiCopiado ?? 'Conteúdo'} copiado` : '';

  if (compact) {
    return (
      <span className="inline-flex items-center">
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 border border-dark/15 bg-muted px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-dark/75 transition-colors hover:text-accent"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? 'Copiado' : label}
        </button>
        <span role="status" aria-live="polite" className="sr-only">
          {anuncio}
        </span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center">
      <Button variant="outline" onClick={copy}>
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? 'Copiado' : label}
      </Button>
      <span role="status" aria-live="polite" className="sr-only">
        {anuncio}
      </span>
    </span>
  );
}

/** Divisor com rótulo centralizado — padrão de seção da referência. */
export function SectionRule({ children }: { children: ReactNode }) {
  return (
    <div className="mb-8 flex items-center gap-4">
      <span className="h-px w-10 bg-accent/40" />
      <MicroLabel>{children}</MicroLabel>
      <span className="h-px flex-1 bg-dark/10" />
    </div>
  );
}

/**
 * Um interruptor.
 *
 * Antes existiam trinta `<input type="checkbox">` crus espalhados e nenhum
 * componente. Este é o primeiro, e o motivo de ele nascer aqui é o estúdio do
 * player: uma tela com quinze escolhas de ligar/desligar em que cada uma tivesse
 * um desenho diferente seria ilegível.
 */
export function Chave({
  ligado,
  onMudar,
  rotulo,
  nota,
  desabilitado,
  descritoPor,
  dica,
}: {
  ligado: boolean;
  onMudar: (v: boolean) => void;
  rotulo: ReactNode;
  nota?: ReactNode;
  desabilitado?: boolean;
  /**
   * Aditivos de 17/09/2026 (agente uiux, Comunidade): o `id` do texto que
   * explica a consequência (`aria-describedby`) e a dica de pairar. Sem eles
   * a chave desenha-se como antes.
   */
  descritoPor?: string;
  dica?: string;
}) {
  return (
    <div className="space-y-1">
      <button
        type="button"
        role="switch"
        aria-checked={ligado}
        aria-describedby={descritoPor}
        title={dica}
        disabled={desabilitado}
        onClick={() => onMudar(!ligado)}
        className="flex w-full items-center gap-2.5 rounded-vst text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-40"
      >
        <span
          className={clsx(
            'relative h-4 w-7 shrink-0 rounded-full transition-colors',
            ligado ? 'bg-accent' : 'bg-dark/15',
          )}
        >
          {/* A bolinha chega com um passo de mola (160 ms); quem pede menos
              movimento vê-a saltar sem curva. */}
          <span
            className={clsx(
              'absolute top-0.5 h-3 w-3 rounded-full bg-white transition-[left] duration-150 ease-[cubic-bezier(.34,1.45,.64,1)] motion-reduce:transition-none',
              ligado ? 'left-3.5' : 'left-0.5',
            )}
          />
        </span>
        <span className="text-xs font-medium text-dark/75">{rotulo}</span>
      </button>
      {nota && <p className="pl-[38px] text-[11px] leading-relaxed text-dark/75">{nota}</p>}
    </div>
  );
}

/**
 * Escolha de cor — o espectro inteiro, mais a opacidade à parte.
 *
 * O `<input type="color">` do navegador já é o seletor RGB completo e é o único
 * que abre a paleta nativa do sistema, com conta-gotas. O que ele **não** tem é
 * canal alfa, e o véu de uma tela sobreposta precisa dele — daí a régua ao lado,
 * que compõe o oitavo dígito do hexadecimal.
 *
 * Sem `permitirVazio`, "nenhuma cor" deixa de existir e todo campo passa a ter
 * uma cor definida, o que engorda a configuração de quem só queria o padrão.
 *
 * ## Por que existe `alvo` além de `label`
 *
 * Porque quem já tem um rótulo por fora passava `label=""` — e o nome acessível
 * da régua saía **"Opacidade de "**, uma preposição solta. Não é preciosismo de
 * leitor de tela: o fio de opacidade não tinha nome nenhum na tela tampouco, e
 * um operador chegou a um fundo de 20% de opacidade sem saber o que estava
 * mexendo, publicando uma página a 1,05:1 de contraste. `alvo` nomeia o que a
 * cor pinta sem repetir o rótulo do campo, e a régua ganhou o número visível.
 */
export function SeletorDeCor({
  label,
  alvo,
  value,
  onChange,
  aoTerminar,
  comAlfa,
  permitirVazio,
  className,
  vozDoRotulo = 'versalete',
  classes,
}: {
  label: string;
  /** O que esta cor pinta, para os nomes acessíveis. Cai em `label`. */
  alvo?: string;
  /** A classe da linha inteira — espaçamento, largura, alinhamento. */
  className?: string;
  /**
   * ── A PEÇA DEIXA-SE VESTIR, EM VEZ DE SE DEIXAR APAGAR ────────────────────
   *
   * *derrubada por ordem do dono de 13/09/2026 (agente uiux): a peça aceitava
   * zero classe, e a saída de quem precisava de outra tipografia era apagá-la.*
   *
   * Não aceitando classe nenhuma, ela obrigava quem a usa a **calar** o que não
   * conseguia mudar: `pele.tsx` do V-Forms passa `label=""` e `alvo="Cor
   * primária"` nos quatro campos de cor da aba `Design` só para o versalete
   * gravado não sair, e reescreve o nome visível por fora, em caixa baixa. Isso
   * custa duas coisas: o rótulo vira um `<span>` vazio que ninguém sabe porque
   * existe, e o nome acessível passa a depender de uma prop (`alvo`) que quem
   * arrumar as props um dia apaga achando que é repetição — deixando os dois
   * controlos sem nome nenhum e a tela igualzinha.
   *
   * Com `vozDoRotulo` o nome **veste-se**: quem quer caixa baixa pede caixa
   * baixa, e o `label` volta a carregar o nome. O `label=""` continua a
   * funcionar para quem já o escreveu — o `<span>` continua no DOM, porque há
   * cerca a contá-lo em `pages/cliente/formularios`, e um `<span>` de bloco sem
   * texto não pinta pixel nenhum.
   *
   * Chave errada aqui não passa calada: o objecto é fechado, e `classes={{
   * rotulos: … }}` é erro de compilação e não campo descartado em silêncio.
   */
  classes?: {
    /** O `<input type="color">` — o quadrado que abre a janela do sistema. */
    espectro?: string;
    /** O campo onde se digita/cola o hexadecimal. */
    hex?: string;
    /** A régua de opacidade, quando `comAlfa`. */
    alfa?: string;
  };
  /**
   * A VOZ do rótulo — e por que ela é uma palavra, e não uma classe.
   *
   * Seria natural abrir `classes.rotulo` como as outras três. **Não se pode**,
   * e a razão é medível: `mesclar` arbitra eixo por NOME de utilitária, e
   * `grupoDe('uppercase')` devolve `null` — `text-transform` não está na tabela
   * de eixos de `lib/mesclar.ts`, porque `uppercase` e `normal-case` não têm
   * prefixo. Uma classe de quem chama nesse eixo **não derrubaria** o
   * `uppercase` da peça: as duas têm especificidade (0,1,0) e quem ganha é a
   * ordem da folha. É exactamente a armadilha que
   * `a-peca-nao-engole-a-classe-do-chamador` existe para prender, e abrir um
   * slot que só funciona em dois dos três eixos do rótulo é armá-la.
   *
   * Duas vozes nomeadas não têm esse problema, porque não há disputa: a peça
   * escreve UMA das duas strings, inteira.
   *
   * - `versalete` — o de fábrica, gravado, para o rótulo que é uma rubrica;
   * - `texto` — caixa baixa, `tracking` de leitura. É letra por letra o
   *   `ROTULO_NA_MOLDURA` de `pages/cliente/formularios/pele.tsx`, que hoje é
   *   escrito POR FORA depois de calar este. Quem o adoptar apaga aquele
   *   `<span>` e devolve o `label` a esta peça.
   */
  vozDoRotulo?: 'versalete' | 'texto';
  value: string | undefined;
  /**
   * `origem` diz QUAL dos dois controles mexeu, e existe por causa do `C13`.
   *
   * O espectro é o `<input type="color">` do sistema: o arrasto acontece numa
   * janela do sistema operacional e não devolve `pointerup` nenhum do nosso
   * lado, então quem agrupa passos de desfazer só consegue fechar o gesto dele
   * pelo relógio. A régua de opacidade é um `<input type="range">` daqui e
   * devolve `pointerup` — nela o relógio é justamente o que parte um arrasto
   * lento em vários passos. Sem esta distinção, o chamador teria de escolher um
   * comportamento errado para um dos dois.
   */
  onChange: (v: string | undefined, origem?: 'espectro' | 'alfa' | 'hex') => void;
  /** O gesto acabou — usado por quem agrupa passos de desfazer. */
  aoTerminar?: () => void;
  comAlfa?: boolean;
  permitirVazio?: boolean;
}) {
  const base = (value ?? '#ffffff').slice(0, 7);
  /*
   * `C31` — **a opacidade mora no BYTE, que é o domínio que ela tem de verdade.**
   *
   * Ela era uma fração de 0 a 1 com passo 0,05, e o hexadecimal guarda um byte:
   * dois domínios diferentes para o mesmo valor, com 256 valores possíveis e 21
   * alcançáveis. O retrato medido em produção é o resumo do defeito —
   * `#22201d1f` servido (alfa 31/255 ≈ 12%), rótulo dizendo `OPACIDADE 12%`,
   * `input.value === "0.1"` porque o navegador encaixa o valor na grade do
   * passo, e `step = 0.05`: **tocar a régua para qualquer lado gravava 10% ou
   * 15%**, destruindo um valor que ninguém pediu para mudar, com o número ao
   * lado nunca avisando.
   *
   * Com o byte, todo valor que o hexadecimal consegue expressar é alcançável
   * pela régua, e mexer nela nunca salta. O rótulo continua em porcento porque
   * é o que o operador pensa — e a precisão dele (1%) é MAIS GROSSA que o passo
   * (≈0,4%), que é o lado certo da desigualdade que `C31` cobra. Quem lê por
   * leitor de tela recebe o porcento em `aria-valuetext`, e não o "31".
   */
  const byte = value && value.length === 9 ? parseInt(value.slice(7, 9), 16) : 255;
  const porcento = Math.round((byte / 255) * 100);
  const nome = (alvo ?? label).trim();

  const compor = (novaBase: string, novoByte: number) => {
    if (!comAlfa || novoByte >= 255) return novaBase;
    return novaBase + novoByte.toString(16).padStart(2, '0');
  };

  /*
   * `T3` — **o campo onde se digita a cor.**
   *
   * Os seis valores da paleta eram `SPAN` não editáveis: o único caminho para
   * uma cor era o espectro do sistema, e quem chega com a paleta da marca em
   * hexadecimal — que é como toda marca a entrega — **não tinha onde colar**.
   * `T3` promete "criar um token nomeado", e nomear pressupõe poder DIZER o
   * valor; a §51.1 registrou a metade positiva dela como nunca conferida, e
   * esta era a razão concreta.
   *
   * O texto digitado vive em estado próprio enquanto o campo está em uso: sem
   * isso, apagar um caractere para corrigir o terceiro dígito reescreveria o
   * campo com a cor anterior a cada tecla. Só o que casa com a forma sobe; o
   * resto fica na tela sendo digitado, e o `onBlur` devolve o valor real.
   */
  const [digitado, setDigitado] = useState<string | null>(null);
  const naTela = digitado ?? value ?? '';

  const lerHex = (texto: string): string | undefined => {
    const s = texto.trim().replace(/^#/, '').toLowerCase();
    /* Três dígitos porque é a forma que a web escreve há trinta anos e é a que
       vem colada de folha de estilo alheia. */
    const cheio =
      s.length === 3 && /^[0-9a-f]{3}$/.test(s)
        ? `${s[0]!}${s[0]!}${s[1]!}${s[1]!}${s[2]!}${s[2]!}`
        : /^[0-9a-f]{6}$/.test(s) || /^[0-9a-f]{8}$/.test(s)
          ? s
          : null;
    if (cheio === null) return undefined;
    /* Alfa colado num campo que não tem opacidade seria um oitavo dígito que a
       tela nunca mostra e o renderizador aplica. Corta. */
    return `#${comAlfa ? cheio : cheio.slice(0, 6)}`;
  };

  return (
    <div className={mesclar('flex items-center gap-3', className)}>
      {/*
       * `rounded-vst` — **o raio que o painel já tem, e que só o espectro não
       * tinha.**
       *
       * Não é token novo: `--radius-vst` vale 3 px no `index.css` desde sempre,
       * e é o raio que o `Toggle` desta mesma folha usa. O quadrado de canto
       * vivo ao lado de uma amostra de cor lê-se como sobra de renderização do
       * navegador — que é literalmente o que era, porque o `<input
       * type="color">` desenha a amostra num pseudo-elemento com borda e
       * enchimento próprios. Os três `::-…-color-swatch` tiram essa segunda
       * borda: a moldura passa a ser UMA, a nossa, e a cor enche-a.
       */}
      <input
        type="color"
        value={base}
        onChange={(e) => onChange(compor(e.target.value, byte), 'espectro')}
        onBlur={aoTerminar}
        className={mesclar(
          'h-9 w-12 shrink-0 cursor-pointer rounded-vst border border-dark/15 bg-muted p-1',
          'transition-colors duration-200 hover:border-dark/30 focus:border-accent focus:outline-none',
          '[&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-0 [&::-moz-color-swatch]:border-0',
          classes?.espectro,
        )}
        aria-label={nome || 'Cor'}
      />
      <div className="min-w-0 flex-1">
        <span
          className={
            vozDoRotulo === 'texto'
              ? 'block text-[11px] leading-none tracking-normal text-dark/75'
              : 'block text-[10px] font-semibold uppercase tracking-[0.15em] text-dark/75'
          }
        >
          {label}
        </span>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={naTela}
            spellCheck={false}
            autoComplete="off"
            inputMode="text"
            maxLength={9}
            size={9}
            placeholder="padrão"
            aria-label={nome ? `Hexadecimal de ${nome}` : 'Hexadecimal da cor'}
            onChange={(e) => {
              setDigitado(e.target.value);
              const lido = lerHex(e.target.value);
              if (lido) onChange(lido, 'hex');
            }}
            onBlur={() => {
              setDigitado(null);
              aoTerminar?.();
            }}
            /*
             * **O campo parece um campo antes de alguém lhe tocar.**
             *
             * Era `border-transparent bg-transparent`, e só ganhava borda no
             * `hover` e fundo no `focus`: em repouso — que é o estado em que a
             * tela é lida — ele era texto cinzento solto ao lado de um
             * quadrado. Isto é o oposto do que `T3` promete: quem chega com a
             * paleta da marca em hexadecimal precisa de VER onde colar, e um
             * campo que só existe depois de o rato passar por cima não existe
             * para quem não passa o rato por cima. Mesma borda, mesmo fundo e
             * mesma duração do `Input` da casa — nenhum token novo.
             *
             * `text-dark` e não `text-dark/75`: o hexadecimal é o VALOR, e
             * valor não se escreve mais apagado que a moldura que o guarda.
             */
            /*
             * `13ch` — **o campo cabe o valor mais longo que ele aceita.**
             *
             * Eram `10ch`, e `10ch` nunca coube: `box-sizing: border-box` (o
             * `preflight` do Tailwind põe-no em tudo) desconta o enchimento e a
             * borda da largura, então `10ch` de caixa davam ~8,6 caracteres de
             * texto — e o valor com alfa tem **9** (`#030712aa`). O retrato a
             * 4× mostrou o último `a` cortado ao meio. Não é preciosismo: o
             * campo que mostra sete dos nove dígitos do valor servido é o campo
             * que faz o operador copiar uma cor errada.
             *
             * Treze e não onze porque o enchimento cresceu com a borda: 13ch de
             * caixa, menos 16 px de `px-2`, menos 2 px de borda, dão folga para
             * os nove com o cursor ao lado.
             */
            className={mesclar(
              'w-[13ch] shrink-0 rounded-vst border border-dark/15 bg-muted px-2 py-1 font-mono text-xs text-dark',
              'transition-colors duration-200 hover:border-dark/30 focus:border-accent focus:outline-none',
              classes?.hex,
            )}
          />
          {permitirVazio && value && (
            <button
              type="button"
              onClick={() => onChange(undefined)}
              className="text-[10px] uppercase tracking-widest text-dark/75 hover:text-error"
            >
              limpar
            </button>
          )}
        </div>
        {comAlfa && (
          <label className="mt-1 block">
            {/* O rótulo é VISÍVEL e traz o número: sem ele o fio embaixo do hex
                não se anuncia como opacidade para ninguém. */}
            <span className="text-[10px] uppercase tracking-[0.12em] text-dark/75">
              opacidade {porcento}%
            </span>
            <input
              type="range"
              min={0}
              max={255}
              step={1}
              value={byte}
              aria-label={nome ? `Opacidade de ${nome}` : 'Opacidade'}
              aria-valuetext={`${porcento}%`}
              onChange={(e) => onChange(compor(base, Number(e.target.value)), 'alfa')}
              onPointerUp={aoTerminar}
              onPointerCancel={aoTerminar}
              onKeyUp={aoTerminar}
              onBlur={aoTerminar}
              /* `h-4` e não `h-1`: a trilha de 4 px media 4 px de alvo, e um
                 alvo de 4 px erra por dedo e por rato. A altura da caixa é o
                 alvo; a trilha desenhada continua fina. */
              className={mesclar('h-4 w-full accent-accent', classes?.alfa)}
            />
          </label>
        )}
      </div>
    </div>
  );
}

/**
 * O texto de uma frase do CORE, com `**` virando negrito de verdade.
 *
 * ## Por que ele existe, e o que ele conserta
 *
 * As frases que decidem alguma coisa moram no core — a do que fica acima do teto
 * na descida, a de apagar um blog, a de desativar uma pessoa — e elas marcam com
 * `**` a palavra que não pode passar batida (*«**DEFINITIVA**»*, *«**Nada é
 * apagado**»*). A tela de descer imprimia a string crua, e o operador lia
 * `**não** é escolha sua` com os asteriscos na cara: a ênfase que existia para
 * segurar o olho virava ruído que ensina a não ler.
 *
 * **A marcação fica no core de propósito.** Ela é da FRASE, e não da tela: quem
 * escreve a regra sabe qual palavra pesa, e três telas decidindo isso por conta
 * própria dariam três ênfases diferentes para o mesmo fato.
 *
 * Só `**`, e nada de markdown: um parser aqui seria uma dependência inteira para
 * resolver a única marca que estas frases usam — e a segunda marca que alguém
 * quiser é uma conversa, não um `import`.
 */
export function ComEnfase({ texto, className }: { texto: string; className?: string }) {
  const pedacos = texto.split('**');
  return (
    <span className={className}>
      {pedacos.map((p, i) =>
        /* Ímpar é o que estava entre as marcas. Um `**` solto (número ímpar de
           marcas) deixa o resto em negrito em vez de imprimir asterisco — errar
           para o lado do texto legível. */
        i % 2 === 1 ? (
          <strong key={i} className="font-semibold text-dark">
            {p}
          </strong>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * O CAMPO DE VÁRIOS VALORES
 * ──────────────────────────────────────────────────────────────────────────── */

/** Uma entrada do dicionário: `rotulo` é o que se lê, `valor` é o que se grava. */
export interface OpcaoDeValor {
  valor: string;
  rotulo: string;
}

/**
 * ── «BRASIL, PORTUGAL OU ANGOLA» NUMA LINHA SÓ ──────────────────────────────
 *
 * Decisão nº 45 do `forma-juiz` (§24.4, 29/08/2026). Ela nasce **aqui** e não na
 * tela que a pediu, e a frase dele é a razão: *«a peça nasce em `ui.tsx`, não em
 * `Segmentos.tsx`, e é UMA peça com dois modos»*. Uma peça por sinal seria o
 * produto cartesiano — quinze controles a divergir um do outro —, e este
 * arquivo tem doze worktrees vivas a disputá-lo justamente porque é aqui que a
 * casa combina o vocabulário.
 *
 * ## O defeito que ela conserta, e ele é mudo
 *
 * Digitar «Brasil» num campo de país grava um segmento que **nunca casa com
 * ninguém, e não dá erro nenhum** — o avaliador compara com `br`. O operador
 * conclui que o público não existe quando o que não existe é o valor. Medido no
 * V-Rotas: **3 campos de lista em 15, 0 checagens de forma ISO-2**.
 *
 * ## Os dois modos, e quem os escolhe é o descritor do campo — nunca a tela
 *
 * - **`opcoes` presente = lista fechada.** O campo **filtra**, e o texto
 *   digitado **nunca vira valor**: só entra o que foi escolhido da lista. É a
 *   Lei A do §24.11, e é a única forma de o valor-fantasma não nascer.
 * - **`opcoes` ausente = texto livre**, e quem chama **declara-o por escrito**
 *   no `hint` do `Field`. Um campo sem dicionário que se comportasse como lista
 *   fechada recusaria o que a pessoa sabe e a tela não — e há dez sinais assim
 *   no V-Rotas, do caminho ao parâmetro de URL.
 *
 * ## A lista confessa o tamanho
 *
 * `Intl.supportedValuesOf('region')` devolve perto de 250 países. Mostrar doze
 * e calar o resto ensina que a lista é curta, e quem não vê «Angola» conclui
 * que ele não é aceite. «12 de 249» é a terceira cláusula da Lei A, e custa uma
 * linha.
 *
 * ## O valor que já estava lá e nunca casou aparece VERMELHO
 *
 * Um segmento gravado antes desta peça pode ter `Brasil` escrito à mão. Abrir e
 * pintá-lo como um valor qualquer seria a tela a dizer que está tudo bem com a
 * regra que nunca serviu ninguém. `error` é cor de **estado**, e este é um
 * estado: a condição está salva e não casa. A peça existe para esse valor não
 * nascer; enquanto ele existir, ela nomeia-o.
 *
 * ## Nenhum token novo
 *
 * A moldura é a do `Input` (`border-dark/15 bg-muted`), a ficha é `bg-surface`
 * com a borda da casa, e a lista flutuante é **a mesma marcação de
 * `CampoComSugestao`** — inclusive o `onMouseDown` em vez de `onClick`, que lá
 * foi aprendido à força: o clique tira o foco do campo antes de o `click`
 * disparar, e a lista fechava sem escolher.
 */
export function CampoDeValores({
  valores,
  onChange,
  opcoes,
  aberta = false,
  ordenavel = false,
  placeholder,
  disabled = false,
  mono = false,
  teto = 12,
  vazio = 'Nada com esse nome',
  id,
  'aria-label': rotuloAcessivel,
}: {
  valores: string[];
  onChange: (valores: string[]) => void;
  /** Presente = lista fechada. Ausente = texto livre, e quem chama declara-o. */
  opcoes?: OpcaoDeValor[];
  /**
   * ── O TERCEIRO MODO: LISTA ABERTA, COM O QUE ESTÁ FORA DELA DECLARADO ─────
   *
   * `opcoes` + `aberta` = o campo **sugere** do catálogo e **aceita** o que não
   * está nele, mas nomeia o que aceitou. Só tem sentido com `opcoes`; sem elas
   * não há catálogo do qual estar fora, e o campo é texto livre como sempre.
   *
   * ## Por que os dois modos não bastavam
   *
   * O campo «Eventos de conversão» da tela de Produtos precisa das duas metades
   * ao mesmo tempo, e nenhum dos dois modos as dá:
   *
   * - **fechado** recusaria um nome legítimo. Uma oferta que estreia amanhã
   *   aponta para um evento que ainda não disparou, e o catálogo é medido do que
   *   já chegou — a lista fechada transformaria a sugestão em cerca.
   * - **livre** é o defeito de hoje: 32 nomes servidos, 3 alcançados, e os
   *   outros 29 só por memória.
   *
   * ## E por que «fora do catálogo» não é o vermelho de `fantasma`
   *
   * `fantasma` diz **«não casa»** — é um estado de erro, e é verdade numa lista
   * fechada, onde o avaliador compara com o código e um valor de fora nunca
   * casaria com ninguém. Aqui é falso: um nome de evento fora do catálogo casa
   * perfeitamente assim que o evento disparar. Pintá-lo de `error` seria a peça
   * a mentir sobre o estado, que é o oposto do que ela existe para fazer.
   *
   * O que ele ganha é uma **marca neutra** e uma palavra — o operador fica a
   * saber que escreveu algo que a medição ainda não viu, sem ser acusado de
   * erro. Nenhum token novo: é a mesma borda e a mesma tinta do `Badge`
   * `neutral`.
   */
  aberta?: boolean;
  /**
   * A ordem dos valores é DADO, e por isso se pode mexer nela.
   *
   * Existe para um caso e ele é real: os passos de um funil. `PageView →
   * InitiateCheckout → Purchase` e a ordem inversa são funis diferentes, e a
   * peça guardava a ordem de inserção sem dar gesto nenhum para a corrigir —
   * quem se enganasse no terceiro passo tinha de apagar os três.
   *
   * Ligado, cada ficha ganha a **posição** e dois gestos de mover. Desligado (o
   * padrão) nada muda, e é o que todas as chamadas de hoje continuam a ver.
   */
  ordenavel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Códigos e caminhos em monoespaçada; nomes de gente em texto normal. */
  mono?: boolean;
  /** Quantas opções a lista mostra antes de confessar quantas ficaram de fora. */
  teto?: number;
  vazio?: string;
  id?: string;
  'aria-label'?: string;
}) {
  /* `catalogo` = há dicionário, e é ele que manda na LISTA (sugestão, filtro,
     teclado). `fechada` = a lista também PROÍBE o que não está nela. Enquanto
     os dois eram a mesma variável não havia como sugerir sem proibir. */
  const catalogo = opcoes !== undefined;
  const fechada = catalogo && !aberta;
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const caixa = useRef<HTMLDivElement>(null);

  /* O dicionário indexado uma vez: a ficha procura o rótulo a cada pintura, e
     `find` numa lista de 249 países por ficha é a conta que não precisa. */
  const porValor = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const o of opcoes ?? []) mapa.set(o.valor, o.rotulo);
    return mapa;
  }, [opcoes]);

  /* O que já foi escolhido sai da lista: reoferecê-lo é oferecer um gesto que
     não faz nada, e o `adicionar` abaixo recusá-lo-ia em silêncio. */
  const candidatos = useMemo(() => {
    const t = termo.trim().toLowerCase();
    return (opcoes ?? []).filter(
      (o) =>
        !valores.includes(o.valor) &&
        (t === '' || o.rotulo.toLowerCase().includes(t) || o.valor.toLowerCase().includes(t)),
    );
  }, [opcoes, valores, termo]);

  const visiveis = candidatos.slice(0, teto);

  useEffect(() => setAtivo(0), [termo]);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const adicionar = (v: string) => {
    const limpo = v.trim();
    if (!limpo || valores.includes(limpo)) return;
    onChange([...valores, limpo]);
    setTermo('');
  };

  const remover = (v: string) => onChange(valores.filter((x) => x !== v));

  return (
    <div className="relative" ref={caixa} data-campo-de-valores={fechada ? 'lista' : 'livre'}>
      <div
        className={mesclar(
          'flex w-full flex-wrap items-center gap-1.5 border border-dark/15 bg-muted px-2 py-1.5',
          'transition-colors duration-200 focus-within:border-accent',
          disabled && 'cursor-not-allowed opacity-40',
        )}
      >
        {valores.map((v, i) => {
          /* Numa lista fechada, valor sem rótulo é valor que o avaliador nunca
             casa. Ver o bloco de cima: ele é nomeado, não escondido. */
          const rotulo = porValor.get(v);
          const fantasma = fechada && rotulo === undefined;
          /* Lista ABERTA: fora do catálogo não é erro — é um valor que a medição
             ainda não viu. Marca neutra e uma palavra, nunca o vermelho de
             `fantasma`. Ver o bloco de `aberta`, acima. */
          const foraDoCatalogo = catalogo && aberta && rotulo === undefined;
          const mover = (destino: number) => {
            if (destino < 0 || destino >= valores.length) return;
            const proximo = [...valores];
            const [tirado] = proximo.splice(i, 1);
            proximo.splice(destino, 0, tirado!);
            onChange(proximo);
          };
          return (
            <span
              key={v}
              data-valor={v}
              data-fantasma={fantasma ? 'sim' : undefined}
              data-fora-do-catalogo={foraDoCatalogo ? 'sim' : undefined}
              data-posicao={ordenavel ? i + 1 : undefined}
              className={mesclar(
                'inline-flex items-center gap-1.5 border px-2 py-1 text-xs',
                mono && 'font-mono',
                fantasma
                  ? 'border-error/30 bg-error/10 text-error'
                  : 'border-dark/20 bg-surface text-dark',
              )}
            >
              {/* A POSIÇÃO, e ela só existe onde a ordem é dado. Um número
                  ordinal numa lista cuja ordem não significa nada seria a
                  decoração que finge estrutura. */}
              {ordenavel && (
                <span aria-hidden className="text-dark/75 tabular-nums">
                  {i + 1}
                </span>
              )}
              {rotulo ?? v}
              {fantasma && (
                <span className="text-[10px] uppercase tracking-[0.12em]">não casa</span>
              )}
              {foraDoCatalogo && (
                <span className="text-[10px] uppercase tracking-[0.12em] text-dark/75">
                  fora do catálogo
                </span>
              )}
              {ordenavel && (
                <>
                  <button
                    type="button"
                    disabled={disabled || i === 0}
                    onClick={() => mover(i - 1)}
                    aria-label={`Mover ${rotulo ?? v} para antes`}
                    className="text-current opacity-60 transition-opacity hover:opacity-100 disabled:opacity-20"
                  >
                    <ChevronLeft size={11} />
                  </button>
                  <button
                    type="button"
                    disabled={disabled || i === valores.length - 1}
                    onClick={() => mover(i + 1)}
                    aria-label={`Mover ${rotulo ?? v} para depois`}
                    className="text-current opacity-60 transition-opacity hover:opacity-100 disabled:opacity-20"
                  >
                    <ChevronRight size={11} />
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={disabled}
                onClick={() => remover(v)}
                aria-label={`Tirar ${rotulo ?? v}`}
                className="text-current opacity-60 transition-opacity hover:opacity-100"
              >
                <X size={11} />
              </button>
            </span>
          );
        })}

        <input
          id={id}
          aria-label={rotuloAcessivel}
          value={termo}
          disabled={disabled}
          placeholder={valores.length === 0 ? placeholder : undefined}
          role={catalogo ? 'combobox' : undefined}
          aria-expanded={catalogo ? aberto : undefined}
          aria-autocomplete={catalogo ? 'list' : undefined}
          onChange={(e) => {
            const bruto = e.target.value;
            /* Em texto livre a vírgula fecha a ficha: é como a pessoa escreve
               uma lista, e é exactamente como a tela antiga gravava tudo numa
               string só. */
            if (!fechada && bruto.includes(',')) {
              const partes = bruto.split(',');
              const ultimo = partes.pop() ?? '';
              /*
               * Acumula e avisa UMA vez.
               *
               * Chamar `adicionar` num laço perdia tudo menos o último: cada
               * chamada monta `[...valores, novo]` a partir do MESMO `valores`
               * desta pintura, e o `onChange` seguinte apagava o anterior. Colar
               * «br,pt,ao» gravava só `ao` — e era mudo, que é a mesma doença
               * que esta peça veio matar. Apanhado pela cerca da peça, não na
               * tela.
               */
              const novos = partes
                .map((p) => p.trim())
                .filter((p) => p !== '' && !valores.includes(p));
              if (novos.length > 0) onChange([...valores, ...new Set(novos)]);
              setTermo(ultimo);
              return;
            }
            setTermo(bruto);
            if (catalogo) setAberto(true);
          }}
          onFocus={() => catalogo && setAberto(true)}
          onBlur={() => {
            /* Texto livre confirma ao sair do campo: quem escreve e clica em
               «Criar segmento» perderia o que escreveu, e o botão que come o
               último valor é o defeito clássico desta peça. */
            if (!fechada) adicionar(termo);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && termo === '' && valores.length > 0) {
              remover(valores[valores.length - 1]!);
              return;
            }
            if (catalogo) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setAberto(true);
                setAtivo((i) => Math.min(i + 1, visiveis.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setAtivo((i) => Math.max(i - 1, 0));
              } else if (e.key === 'Enter') {
                /* Sempre `preventDefault` numa lista fechada, mesmo sem opção
                   marcada: o Enter que «não fez nada» é o gesto em que a pessoa
                   acredita ter escolhido. Ver a frase de recusa abaixo. */
                e.preventDefault();
                if (aberto && visiveis[ativo]) adicionar(visiveis[ativo]!.valor);
                /* Lista ABERTA: sem opção marcada, o Enter grava o que foi
                   escrito. É a diferença inteira entre os dois modos, e sem esta
                   linha a lista aberta comportar-se-ia como fechada e recusaria
                   em silêncio o valor legítimo que ela existe para aceitar. */
                else if (!fechada) adicionar(termo);
              } else if (e.key === 'Escape') {
                setAberto(false);
              }
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              adicionar(termo);
            }
          }}
          className={mesclar(
            'min-w-[10ch] flex-1 bg-transparent px-2 py-1.5 text-sm text-dark',
            'placeholder:text-dark/45 focus:outline-none',
            mono && 'font-mono text-xs',
            disabled && 'cursor-not-allowed',
          )}
        />
      </div>

      {/*
       * A recusa aparece no bloco onde o gesto foi dado — Lei B do §24.11. Sem
       * ela, digitar «Brasil» e carregar Enter não faz nada e não diz nada, que
       * é a mesma mudez que a peça veio matar.
       */}
      {fechada && termo.trim() !== '' && candidatos.length === 0 && (
        <p className="mt-1.5 text-xs leading-relaxed text-dark/75">
          «{termo.trim()}» não está na lista, e por isso não se grava: o produto compara o código, e
          um valor fora da lista nunca casaria com ninguém. Escolha um da lista.
        </p>
      )}

      {aberto && catalogo && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto border border-dark/20 bg-surface shadow-lg">
          {visiveis.length === 0 ? (
            <p className="px-3 py-2 text-xs text-dark/75">{vazio}</p>
          ) : (
            <>
              {visiveis.map((o, i) => (
                <button
                  key={o.valor}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    adicionar(o.valor);
                  }}
                  onMouseEnter={() => setAtivo(i)}
                  className={mesclar(
                    'flex w-full items-baseline gap-3 px-3 py-1.5 text-left transition-colors',
                    i === ativo ? 'bg-accent/10' : 'hover:bg-dark/5',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate text-sm">{o.rotulo}</span>
                  <span className="shrink-0 font-mono text-[11px] text-dark/75">{o.valor}</span>
                </button>
              ))}
              {/* A terceira cláusula da Lei A: a lista truncada diz o tamanho. */}
              {candidatos.length > visiveis.length && (
                <p className="border-t border-dark/10 px-3 py-2 text-[11px] text-dark/75">
                  {visiveis.length} de {candidatos.length}. Escreva para estreitar a lista.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * O CAMPO DE DINHEIRO
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Os separadores e as casas de uma moeda, **derivados** — nunca literais.
 *
 * `Intl.NumberFormat(...).formatToParts` diz qual é o grupo e qual é o decimal
 * para aquela moeda no idioma em que o painel é lido, e `resolvedOptions` diz
 * quantas casas a moeda tem. Escrever `'.'` e `','` à mão parece igual e não é:
 * o par escalar desta casa tem **BRL e EUR** gravados hoje (`Preco Portugal
 * Angola` é EUR), e há moedas sem casa decimal nenhuma. Um literal acerta no
 * real e mente no resto.
 */
export function formatoDaMoeda(moeda: string | undefined): {
  grupo: string;
  decimal: string;
  casas: number;
} {
  let fmt: Intl.NumberFormat;
  try {
    fmt = new Intl.NumberFormat('pt-BR', moeda ? { style: 'currency', currency: moeda } : {});
  } catch {
    /* Moeda gravada que o ISO não conhece: o campo continua a funcionar como
       número, e quem recusa a moeda é o `select` ao lado — não este. */
    fmt = new Intl.NumberFormat('pt-BR');
  }
  const partes = fmt.formatToParts(12345.6);
  return {
    grupo: partes.find((p) => p.type === 'group')?.value ?? '.',
    decimal: partes.find((p) => p.type === 'decimal')?.value ?? ',',
    casas: fmt.resolvedOptions().maximumFractionDigits ?? 2,
  };
}

/**
 * ── LER DINHEIRO ESCRITO POR GENTE ──────────────────────────────────────────
 *
 * Quatro formas chegam a este campo, e as quatro têm de dar o MESMO número:
 * `1.297,00`, `1297.00`, `R$ 1.297` e `1297` todas valem **1297**. Alguém vai
 * colar cada uma delas, e um leitor que acerte em três e erre numa grava um
 * preço errado sem dar erro nenhum — a pior forma de errar dinheiro.
 *
 * A regra de desempate, e é a única parte difícil:
 *
 * - **os dois separadores presentes** o da direita é o decimal (`1.297,00` e
 *   `1,297.00` funcionam os dois);
 * - **um só, repetido** é agrupamento (`1.297.500` = 1 297 500);
 * - **um só, uma vez** é agrupamento se tiver exactamente 3 dígitos à direita
 *   (`1.297` = 1297), e decimal caso contrário (`18,79`; `1297.00` = 1297).
 *
 * Vazio devolve `null`, e `null` é **«não declarei»** — nunca zero. É a `P11`:
 * zero afirmaria que a oferta é grátis, e o custo nulo desta base aparece vazio
 * hoje de propósito.
 */
export function lerDinheiro(texto: string): number | null {
  const limpo = texto.replace(/[^\d.,-]/g, '');
  if (limpo.replace(/[.,-]/g, '') === '') return null;

  const pontos = (limpo.match(/\./g) ?? []).length;
  const virgulas = (limpo.match(/,/g) ?? []).length;
  let corte = -1;
  if (pontos > 0 && virgulas > 0) {
    corte = Math.max(limpo.lastIndexOf('.'), limpo.lastIndexOf(','));
  } else if (pontos + virgulas === 1) {
    const pos = Math.max(limpo.lastIndexOf('.'), limpo.lastIndexOf(','));
    corte = limpo.length - pos - 1 === 3 ? -1 : pos;
  }

  const so = (s: string) => s.replace(/[.,]/g, '');
  const inteiro = corte === -1 ? so(limpo) : so(limpo.slice(0, corte));
  const fracao = corte === -1 ? '' : so(limpo.slice(corte + 1));
  const base = inteiro === '' || inteiro === '-' ? `${inteiro}0` : inteiro;
  const n = Number(`${base}.${fracao || '0'}`);
  return Number.isFinite(n) ? n : null;
}

/** O inteiro agrupado, preservando a fração que a pessoa ainda está a escrever. */
function mascarar(bruto: string, moeda: string | undefined): string {
  const { grupo, decimal, casas } = formatoDaMoeda(moeda);
  const limpo = bruto.replace(/[^\d.,]/g, '');
  if (limpo === '') return '';

  /* O ÚLTIMO separador escrito é o decimal ENQUANTO se escreve — aqui não se
     adivinha como em `lerDinheiro`, porque o `1.` a meio de «1.297» não pode
     virar «1,» debaixo do dedo. */
  const ultimo = Math.max(limpo.lastIndexOf('.'), limpo.lastIndexOf(','));
  const temFracao = ultimo !== -1 && limpo.length - ultimo - 1 <= casas && casas > 0;
  const digitosInteiros = (temFracao ? limpo.slice(0, ultimo) : limpo).replace(/[.,]/g, '');
  const fracao = temFracao ? limpo.slice(ultimo + 1).replace(/[.,]/g, '') : null;

  const agrupado = digitosInteiros.replace(/\B(?=(\d{3})+(?!\d))/g, grupo);
  if (fracao === null) return agrupado;
  return `${agrupado || '0'}${decimal}${fracao}`;
}

/** Quantos dígitos existem até uma posição — a régua para repor o cursor. */
function digitosAte(texto: string, pos: number): number {
  return (texto.slice(0, pos).match(/\d/g) ?? []).length;
}

/** A posição logo depois do n-ésimo dígito. */
function posDepoisDe(texto: string, digitos: number): number {
  if (digitos <= 0) return 0;
  let vistos = 0;
  for (let i = 0; i < texto.length; i += 1) {
    if (/\d/.test(texto[i]!)) {
      vistos += 1;
      if (vistos === digitos) return i + 1;
    }
  }
  return texto.length;
}
/**
 * A cadeia canónica na forma que a máscara entende, com as casas da moeda.
 *
 * ── POR QUE `Intl` E NÃO `toFixed` ─────────────────────────────────────────
 *
 * A primeira versão era `n.toFixed(casas).replace('.', decimal)`, e a cerca
 * `o-numero-fala-uma-lingua-so` reprovou-a com razão: **`toFixed` devolve ponto
 * decimal em qualquer idioma**, e o `.replace` a seguir é um remendo de locale
 * escrito à mão — exactamente o literal que `formatoDaMoeda` existe para não
 * haver. Num idioma em que o decimal não fosse vírgula, o remendo trocava o
 * separador errado.
 *
 * `useGrouping: false` de propósito: quem agrupa é `mascarar`, logo a seguir, e
 * com o separador daquela moeda. Agrupar duas vezes daria `1..297`.
 */
function paraExibicao(canonico: string, moeda: string | undefined): string {
  if (canonico === '') return '';
  const n = Number(canonico);
  if (!Number.isFinite(n)) return canonico;
  const { casas } = formatoDaMoeda(moeda);
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
    useGrouping: false,
  }).format(n);
}

/**
 * ── O CAMPO DE DINHEIRO, COM MÁSCARA E SEM SETINHAS ─────────────────────────
 *
 * ## O que ele substitui, e por que a correcção anterior não bastou
 *
 * Preço e Custo eram `<Input type="number" step="0.01">`. A leva 7 pagou a
 * `D31` **na exibição** — aparando os zeros de `numeric(18,4)` — e o dono
 * voltou ao mesmo sítio, com captura:
 *
 * ```
 * PREÇO   18790     com setinhas de spinner, sem separador nenhum
 * CUSTO   18,79
 * ```
 *
 * **Dois formatos na mesma tela, e nenhum é máscara.** A causa não era o valor,
 * era o campo: `type="number"` não agrupa milhar (daí `18790`), traz o spinner,
 * e deixa o navegador escolher o separador decimal conforme o idioma (daí a
 * vírgula do custo). Aparar zeros mediu **o número** e não **o campo** — e a
 * cerca que a acompanhava afirma «nenhum passa de 2 casas», o que continua
 * verdadeiro sobre `18790` e continua errado no ecrã.
 *
 * ## O que ele é
 *
 * `type="text"` com `inputMode="decimal"` — sem spinner, e com o teclado
 * numérico certo no telemóvel. A máscara agrupa o milhar **enquanto se digita**,
 * e os separadores saem de `formatoDaMoeda`, derivados da moeda escolhida no
 * campo ao lado. Trocar a moeda com o formulário aberto **remascara** o que já
 * lá está.
 *
 * ## O cursor, que é onde esta classe de peça costuma morrer
 *
 * Reinserir separadores muda os índices, e uma máscara ingénua manda o cursor
 * para o fim a cada tecla — ou impede apagar o último dígito. A régua aqui é o
 * **número de dígitos antes do cursor**: conta-se antes, remascara-se, repõe-se
 * o cursor depois do mesmo dígito.
 *
 * ## O que ele NÃO muda
 *
 * O valor que sai daqui é a cadeia canónica (`'1297.5'`, ponto decimal, sem
 * agrupamento) ou `''` — exactamente o que o formulário já enviava. É a `R3`: a
 * máscara é de entrada e de exibição, e o número gravado é o mesmo.
 */
export function CampoDeDinheiro({
  valor,
  onChange,
  moeda,
  id,
  placeholder,
  'aria-label': rotuloAcessivel,
}: {
  /** A cadeia canónica: `''` = não declarado; `'187.9'` = o número. */
  valor: string;
  onChange: (canonico: string) => void;
  /** A moeda escolhida ao lado. Muda a máscara em tempo real. */
  moeda: string | undefined;
  id?: string;
  placeholder?: string;
  'aria-label'?: string;
}) {
  const campo = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  const [texto, setTexto] = useState(() => mascarar(paraExibicao(valor, moeda), moeda));

  /* O valor pode mudar de fora (abrir outra oferta) e a MOEDA pode mudar com o
     formulário aberto — nos dois casos o que está escrito é remascarado. */
  useEffect(() => {
    setTexto((atual) => {
      const numeroEscrito = lerDinheiro(atual);
      const numeroVindo = valor === '' ? null : Number(valor);
      const remascarado = mascarar(atual, moeda);
      if (numeroEscrito === numeroVindo && atual === remascarado) return atual;
      return mascarar(paraExibicao(valor, moeda), moeda);
    });
  }, [valor, moeda]);

  useEffect(() => {
    if (caret.current !== null && campo.current) {
      campo.current.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
  });

  return (
    <input
      ref={campo}
      id={id}
      /* `text` e não `number`: é o que tira as setinhas e deixa a máscara pôr o
         separador de milhar. `inputMode` abre o teclado numérico no telemóvel. */
      type="text"
      inputMode="decimal"
      autoComplete="off"
      aria-label={rotuloAcessivel}
      value={texto}
      placeholder={placeholder}
      onChange={(e) => {
        const alvo = e.target;
        const antes = digitosAte(alvo.value, alvo.selectionStart ?? alvo.value.length);
        const mascarado = mascarar(alvo.value, moeda);
        caret.current = posDepoisDe(mascarado, antes);
        setTexto(mascarado);
        const n = lerDinheiro(mascarado);
        onChange(n === null ? '' : String(n));
      }}
      onBlur={() => {
        /* Ao sair, assenta na forma canónica da moeda — «1.297,5» vira
           «1.297,50». Durante a digitação isso seria uma casa a aparecer
           debaixo do dedo. */
        const n = lerDinheiro(texto);
        setTexto(n === null ? '' : mascarar(paraExibicao(String(n), moeda), moeda));
      }}
      className={mesclar(
        'w-full border border-dark/15 bg-muted px-3 py-2.5 text-sm text-dark',
        'transition-colors duration-200 placeholder:text-dark/45 focus:border-accent focus:outline-none',
      )}
    />
  );
}


// ─── Dinheiro numa CÉLULA de tabela — as duas decisões de 01/09/2026 ─────────

/**
 * ── A MARCA DA MOEDA NÃO DECLARADA (`D5`) ──────────────────────────────────
 *
 * A linha não informou moeda; a tela exibe na base do cliente **e diz que o
 * fez**. Decisão do dono, com o custo escrito por ele: *«quem não ler a marca
 * lê como real»*. Logo — e esta é a parte que decide se a peça está certa —
 * **a marca é o que torna a decisão legítima**, e uma marca que não se lê
 * devolve a tela ao `?? 'BRL'` que a decisão veio corrigir.
 *
 * ## Três coisas que ela NÃO faz, e cada uma foi medida nesta casa
 *
 * **1. Não é `title` sozinho.** O `Excluir produto` de 15×15 px passou meses
 * invisível com o rótulo só no `title`. A marca é texto no fluxo; o `title`
 * carrega a frase inteira, que é outra camada.
 *
 * **2. Não tem cor semântica.** Âmbar diria «erro», e não é erro: é ausência de
 * declaração, e o dono decidiu exibir assim mesmo. A marca fica na tinta do
 * texto, no piso de contraste da casa (`/75`) — o mesmo do `MicroLabel`, e pela
 * mesma razão pela qual os cabeçalhos subiram de `/45` para `/75`.
 *
 * **3. Não usa sobrescrito Unicode.** O esboço da decisão veio como
 * `ᵐᵒᵉᵈᵃ ⁿᵃᵒ ᵈᵉᶜˡᵃʳᵃᵈᵃ`, e ali está a razão de não o copiar: **não existe `ã`
 * sobrescrito**, e o próprio esboço saiu escrevendo «nao». Uma marca que erra a
 * ortografia da palavra que ela escreve é tênue no pior dos sentidos, e o
 * leitor de tela a soletra. O DESENHO que o dono escolheu — marca pequena, ao
 * lado do número, na mesma linha — está mantido; o que muda é que ela é tipo de
 * verdade.
 */
export function MarcaDeMoedaNaoDeclarada() {
  return (
    <span
      title={FRASE_DA_MOEDA_NAO_DECLARADA}
      /* `whitespace-nowrap`: a marca é uma unidade de leitura. Quebrada ao meio
         («moeda não / declarada») ela vira duas palavras soltas ao lado de um
         número, que é como se lê um defeito de layout e não um aviso. */
      className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.12em] text-dark/75"
    >
      {MARCA_DA_MOEDA_NAO_DECLARADA}
    </span>
  );
}

/**
 * O dinheiro de UMA linha cuja moeda pode não ter sido declarada (`D5`).
 *
 * `moeda` é `event.currency` — anulável de propósito. `base` é
 * `client.base_currency`, e sem as duas não há eixo nenhum: aí sai o traço com
 * a frase, que é a doutrina deste painel desde a leva 7.
 */
export function DinheiroDaLinha({
  valor,
  moeda,
  base,
  semCentavos = false,
  className,
}: {
  valor: number | string | null | undefined;
  /** A moeda DA LINHA. `null` é a resposta comum, e não um erro. */
  moeda: string | null | undefined;
  /** A moeda-base do CLIENTE (`R5`) — o eixo para onde a linha sem moeda cai. */
  base: string | null | undefined;
  semCentavos?: boolean;
  className?: string;
}) {
  const n = typeof valor === 'string' ? Number(valor) : valor;
  const exibida = moedaExibidaNaLinha(moeda, base);

  if (n === null || n === undefined || !Number.isFinite(n) || exibida.forma === 'sem-eixo') {
    return (
      <span
        /* `/75` e não `/45`: o piso de contraste desta casa é 75, e o traço é
           INFORMAÇÃO — ele diz «não há eixo para exibir este valor». Um traço
           que não se lê é o mesmo defeito da marca que não se lê, com outro
           nome. Era `/45`, e a cerca da tinta o apanhou. */
        className={mesclar('text-dark/75', className)}
        title={
          exibida.forma === 'sem-eixo'
            ? 'Nem a linha nem o cliente declararam moeda — não há eixo para exibir este valor.'
            : undefined
        }
      >
        —
      </span>
    );
  }

  return (
    <span className={mesclar('inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5', className)}>
      <span className="tabular-nums">
        {new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: exibida.moeda,
          ...(semCentavos ? { maximumFractionDigits: 0 } : {}),
        }).format(n)}
      </span>
      {exibida.forma === 'caiu-na-base' && <MarcaDeMoedaNaoDeclarada />}
    </span>
  );
}

/**
 * ── A CÉLULA QUE REPARTE (`D4`) ────────────────────────────────────────────
 *
 * Sob mistura, a célula **escreve as duas (ou três) parcelas**, uma por linha.
 * Decisão do dono, com o custo aceito por escrito: **a linha da tabela cresce
 * em altura**.
 *
 * ```
 * │ CAMPANHA        │ RECEITA        │
 * │ facebook / pt2  │ R$ 132,48      │
 * │                 │ € 60,00        │
 * │                 │ US$ 7,00       │
 * ```
 *
 * ## O que a decisão obriga, e cada item tem uma razão medida
 *
 * **Não trunca.** Nem `truncate`, nem `line-clamp`, nem reticências. Truncar
 * dinheiro é mentir com pontinhos: `R$ 132,4…` é um número que não existe, e o
 * operador não tem como saber que falta dígito. Quando não cabe na largura, o
 * valor **quebra** — é para isso que a coluna tem `break-words`.
 *
 * **A altura varia, e isso é informação.** Uma linha alta é uma linha que tem
 * mais a dizer. É o mesmo precedente do `items-start` que outro laço escreveu:
 * alinhar pelo topo faz a coluna vizinha continuar legível enquanto esta cresce.
 *
 * **A ordem é estável entre linhas.** `moedasNaOrdem` é uma ordem TOTAL (base do
 * cliente primeiro, depois alfabética) e não depende da linha — então duas
 * células com moedas diferentes as escrevem na mesma sequência relativa, e o
 * olho desce a coluna sem reaprender a ordem. Sem isso a tabela deixa de ser
 * comparável, que é o ponto de que a decisão do dono vive.
 *
 * ## Onde ela NÃO se aplica
 *
 * **No cartão.** O auditor recomendava repartir em tabela e recusar em cartão;
 * o dono decidiu a **tabela**. O cartão continua com o que a `L10.1` já fez —
 * `n/d` com a frase e o `CartaoDeMoedas` abaixo. Estender esta decisão ao
 * cartão por analogia seria decidir por ele.
 */
export function DinheiroRepartido({
  porMoeda,
  base,
  semCentavos = false,
  className,
}: {
  /**
   * O dinheiro repartido. `undefined` é «a repartição não chegou» (resposta
   * antiga em cache) e `{}` é «sei, e não houve venda» — as duas desenham
   * traço, e a segunda é a verdade sobre o período.
   */
  porMoeda: Record<string, number> | undefined;
  base: string | null | undefined;
  semCentavos?: boolean;
  className?: string;
}) {
  const moedas = moedasNaOrdem(porMoeda, base);

  /* `/75` pelo mesmo motivo do irmão acima: o traço é informação, e o piso da
     casa é 75. */
  if (moedas.length === 0) return <span className="text-dark/75">—</span>;

  return (
    /* `break-words` e nunca `truncate`: ver o cabeçalho. `items-end` alinha os
       valores pela direita da coluna, que é como uma coluna de dinheiro se lê. */
    <span className={mesclar('flex flex-col items-end gap-0.5 break-words', className)}>
      {moedas.map((moeda) => (
        <span key={moeda} className="tabular-nums">
          {new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: moeda,
            ...(semCentavos ? { maximumFractionDigits: 0 } : {}),
          }).format(porMoeda?.[moeda] ?? 0)}
        </span>
      ))}
    </span>
  );
}
